import {
  addPermissionGroupMember as addPermissionGroupMemberInDomain,
  addSeasonMembershipToMemberGroup as addSeasonMembershipToMemberGroupInDomain,
  approveJoinRequest as approveJoinRequestInDomain,
  createChoir as createChoirInDomain,
  createMemberGroup as createMemberGroupInDomain,
  createMemberGroupTag as createMemberGroupTagInDomain,
  createPermissionGroup as createPermissionGroupInDomain,
  createUserAccount,
  deleteInvitationCode as deleteInvitationCodeInDomain,
  deleteMemberGroup as deleteMemberGroupInDomain,
  deleteMemberGroupTag as deleteMemberGroupTagInDomain,
  deletePermissionGroup as deletePermissionGroupInDomain,
  generateInvitationCode as generateInvitationCodeInDomain,
  rejectJoinRequest as rejectJoinRequestInDomain,
  renameMemberGroup as renameMemberGroupInDomain,
  renameMemberGroupTag as renameMemberGroupTagInDomain,
  removePermissionGroupMember as removePermissionGroupMemberInDomain,
  removeSeasonMembershipFromMemberGroup as removeSeasonMembershipFromMemberGroupInDomain,
  setMemberGroupManagerGrant as setMemberGroupManagerGrantInDomain,
  setMemberGroupTagMembership as setMemberGroupTagMembershipInDomain,
  submitJoinRequest as submitJoinRequestInDomain,
  updatePermissionGroup as updatePermissionGroupInDomain,
  type DomainPorts
} from './domain';
import { deriveMemberDisplayId } from './displayIds';
import { normalizeEmail } from './model';
import {
  canCreatorAddFirstMemberToGroup,
  canManageMemberGroupByRoleOrGrant,
  getInvitationCodeUsabilityProblem,
  nextDisplayOrder,
  resolveEffectivePermissionKeys
} from './memberManagementRules';
import type {
  Choir,
  InvitationCode,
  JoinRequest,
  FileUpload,
  MemberGroup,
  MemberGroupTag,
  MemberManagementData,
  Membership,
  PermissionGroup,
  PermissionKey,
  Season,
  SeasonMembership,
  UserAccount
} from './model';
import { normalizeRequiredText } from './model';
import type { AuthPort, DataStore, InvitationCodeGenerator, MaybePromise, SessionPort } from './ports';
import type { AttendanceCategory, AttendanceCategoryStatus, AttendanceRecordStatus, Schedule } from '../schedule/model';
import type {
  Assignment,
  AssignmentDeliveryRule,
  AssignmentDeliveryRuleReviewer,
  AssignmentDeliveryRuleTarget,
  AssignmentSubmissionType
} from '../assignment/model';
import {
  applyAssignmentSubmission,
  assertCanSubmitAssignment,
  normalizeAssignmentDraft,
  resolveDeliveryRules,
  validateAssignmentSubmissionFile
} from '../assignment/rules';
import type {
  AssignmentDeliveryRuleViewSource,
  AssignmentDetail,
  AssignmentListItem,
  AssignmentViewMember,
  ResolvedAssignmentDeliveryRuleView
} from '../assignment/view';
import { projectAssignmentDetail, projectAssignmentListItem } from '../assignment/view';
import {
  createSongScoreUpdatedNotification,
  listNotificationsForMember,
  markNotificationRead as markNotificationReadInDomain
} from '../notification/rules';
import type { NotificationListItem } from '../notification/view';
import type { ResourceFile, ResourceFolder, ResourceFolderPlacement, Song } from '../resources/model';
import {
  attachResourceFile as attachResourceFileInDomain,
  createResourceFolder as createResourceFolderInDomain,
  createSong as createSongInDomain,
  replaceSongCurrentScore
} from '../resources/rules';
import type { ResourceFileListItem, ResourceFolderListItem, SongListItem } from '../resources/view';
import type { ObjectStoragePort, UploadAuthorization } from '../storage/objectStorage';
import { createLocalObjectStoragePort } from '../storage/objectStorage';
import {
  applyScheduleUpdate,
  buildDefaultAttendanceCategories,
  createScheduleEntities,
  markScheduleDeleted,
  normalizeScheduleDraft,
  signAttendanceRecord
} from '../schedule/rules';
import {
  compareSchedulesByStart,
  findActiveSchedulesInWindow,
  findActiveSeasonMembershipForSchedule,
  findAttendanceCategoriesForSeason,
  findAttendanceCategoryForService,
  findAttendanceEventForSchedule,
  findScheduleForService,
  hasEnabledAttendanceCategoryNameCollision,
  needsScheduleReadSettlement,
  parseIsoMillis,
  replaceByAttendanceCategoryId,
  replaceByAttendanceRecordId,
  replaceByScheduleId,
  replaceScheduleAttendanceDraft,
  selectHomeScheduleFromSortedList,
  selectUpcomingSchedulesFromSortedList,
  settleSchedulesIfNeeded,
  toScheduleDetail,
  toScheduleListItem,
  validateScheduleAttendanceDraft
} from './scheduleAppService';

export const MAX_INVITATION_CODE_GENERATION_ATTEMPTS = 5;
export const MARKDOWN_PREVIEW_UNSUPPORTED_MESSAGE = 'Resource file cannot be previewed as Markdown';

export interface MemberManagementRuntime {
  readonly dataStore: DataStore<MemberManagementData>;
  readonly auth: AuthPort;
  readonly session: SessionPort;
  readonly invitationCodeGenerator: InvitationCodeGenerator;
  readonly objectStorage?: ObjectStoragePort;
  readonly ports: DomainPorts;
}

export interface MemberListItem {
  readonly seasonMembershipId: string;
  readonly nickname: string;
  readonly role: 'admin' | 'member';
  readonly memberGroupNames: readonly string[];
}

export type MemberSelectorPurpose = 'member_group_initial_member' | 'member_group_add_member' | 'member_selection';

export type MemberManagementListItem = MemberListItem;

export interface MemberSelectorListItem {
  readonly seasonMembershipId: string;
  readonly nickname: string;
  readonly memberGroupNames: readonly string[];
}

export interface MemberDisplayListItem {
  readonly displayMemberId: string;
  readonly nickname: string;
  readonly memberGroupNames: readonly string[];
}

export interface MemberGroupSummary {
  readonly memberGroupId: string;
  readonly name: string;
  readonly isDefault: boolean;
  readonly memberCount: number;
  readonly canManage: boolean;
}

export interface MemberGroupMemberListItem {
  readonly seasonMembershipId: string;
  readonly nickname: string;
  readonly role: 'admin' | 'member';
  readonly tagIds: readonly string[];
  readonly tagNames: readonly string[];
  readonly isGroupManager: boolean;
  readonly isImplicitAdminManager: boolean;
}

export interface MemberGroupTagListItem {
  readonly memberGroupTagId: string;
  readonly name: string;
  readonly memberCount: number;
}

export interface MemberGroupDetail {
  readonly memberGroupId: string;
  readonly name: string;
  readonly isDefault: boolean;
  readonly memberCount: number;
  readonly canManage: boolean;
  readonly canAddMembers: boolean;
  readonly members: readonly MemberGroupMemberListItem[];
  readonly availableMembersToAdd: readonly MemberListItem[];
  readonly tags: readonly MemberGroupTagListItem[];
}

export interface PermissionGroupSummary {
  readonly permissionGroupId: string;
  readonly name: string;
  readonly permissionKeys: readonly PermissionKey[];
  readonly memberCount: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface PermissionGroupMemberListItem {
  readonly seasonMembershipId: string;
  readonly nickname: string;
  readonly memberGroups: readonly string[];
}

export interface PermissionGroupDetail {
  readonly permissionGroupId: string;
  readonly name: string;
  readonly permissionKeys: readonly PermissionKey[];
  readonly members: readonly PermissionGroupMemberListItem[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface PendingJoinRequestListItem {
  readonly joinRequestId: string;
  readonly userId: string;
  readonly applicantEmail: string;
  readonly choirId: string;
  readonly seasonId: string;
  readonly invitationCodeId: string;
  readonly nickname: string;
  readonly status: 'pending';
  readonly submittedAt: string;
}

export interface ApprovalListItem {
  readonly joinRequestId: string;
  readonly nickname: string;
  readonly applicantEmail: string;
  readonly submittedAt: string;
}

export interface ApprovalMemberGroupChoice {
  readonly memberGroupId: string;
  readonly name: string;
  readonly isDefault: boolean;
  readonly displayOrder: number;
}

export interface ApprovalDetail extends ApprovalListItem {
  readonly memberGroupChoices: readonly ApprovalMemberGroupChoice[];
}

export interface InvitationListItem {
  readonly invitationCodeId: string;
  readonly code: string;
  readonly status: InvitationCode['status'];
  readonly createdAt: string;
  readonly expiresAt: string;
}

export interface EmailIdentification {
  readonly email: string;
  readonly status: 'registered' | 'unregistered';
}

export interface InvitationCodePreview {
  readonly code: string;
  readonly choirId: string;
  readonly choirName: string;
  readonly seasonId: string;
  readonly seasonName: string;
}

export interface ScheduleListItem {
  readonly scheduleId: string;
  readonly title: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly location: string;
  readonly note: string;
  readonly attendanceCategoryName: string | null;
  readonly attendanceStatus: 'not_enabled' | 'not_started' | 'signing' | 'ended';
  readonly canManage: boolean;
}

export interface AttendanceCategoryListItem {
  readonly attendanceCategoryId: string;
  readonly name: string;
  readonly status: AttendanceCategoryStatus;
  readonly displayOrder: number;
}

export interface ScheduleParticipantMemberGroupOption {
  readonly memberGroupId: string;
  readonly name: string;
  readonly isDefault: boolean;
  readonly displayOrder: number;
}

export interface ScheduleEditOptions {
  readonly attendanceCategories: readonly AttendanceCategoryListItem[];
  readonly participantMemberGroups: readonly ScheduleParticipantMemberGroupOption[];
}

export interface ScheduleAttendanceCategory {
  readonly attendanceCategoryId: string;
  readonly name: string;
}

export interface ScheduleParticipantMemberGroup {
  readonly memberGroupId: string;
  readonly name: string;
}

export interface ScheduleAttendanceRecordItem {
  readonly attendanceRecordId: string;
  readonly seasonMembershipId: string;
  readonly nickname: string;
  readonly memberGroupNames: readonly string[];
  readonly status: AttendanceRecordStatus;
  readonly selectedAt: string | null;
  readonly settledAt: string | null;
}

export interface ScheduleDetail extends ScheduleListItem {
  readonly deletedAt: string | null;
  readonly attendance: null | {
    readonly attendanceEventId: string;
    readonly category: ScheduleAttendanceCategory;
    readonly participantMemberGroupIds: readonly string[];
    readonly participantMemberGroups: readonly ScheduleParticipantMemberGroup[];
    readonly onTimeReferenceMinutesFromStart: number;
    readonly lateReferenceMinutesFromStart: number;
    readonly currentUserRecord: ScheduleAttendanceRecordItem | null;
    readonly records: readonly ScheduleAttendanceRecordItem[];
  };
}

export interface ScheduleDraftInput {
  readonly title: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly location: string;
  readonly note?: string;
  readonly attendance: null | {
    readonly attendanceCategoryId: string;
    readonly participantMemberGroupIds: readonly string[];
    readonly onTimeReferenceMinutesFromStart: number;
    readonly lateReferenceMinutesFromStart: number;
  };
}

export interface ScheduleUpdateDraftInput {
  readonly title?: string;
  readonly startsAt?: string;
  readonly endsAt?: string;
  readonly location?: string;
  readonly note?: string;
  readonly attendance?: ScheduleDraftInput['attendance'];
}

type MergedScheduleUpdateDraftInput = Omit<ScheduleDraftInput, 'attendance'> & {
  readonly attendance?: ScheduleDraftInput['attendance'];
};

export interface AssignmentDeliveryRuleDraftInput {
  readonly target: {
    readonly memberGroupIds: readonly string[];
    readonly includeSeasonMembershipIds: readonly string[];
    readonly excludeSeasonMembershipIds: readonly string[];
  };
  readonly reviewers: {
    readonly memberGroupIds: readonly string[];
    readonly memberGroupTagIds: readonly string[];
    readonly seasonMembershipIds: readonly string[];
  };
}

export interface AssignmentDraftInput {
  readonly pieceName: string;
  readonly dueAt: string;
  readonly submissionType: AssignmentSubmissionType;
  readonly description?: string;
  readonly deliveryRules: readonly AssignmentDeliveryRuleDraftInput[];
}

export interface AssignmentUploadIntent {
  readonly fileUploadId: string;
  readonly objectKey: string;
  readonly upload: UploadAuthorization;
}

export interface AssignmentSubmissionResult {
  readonly submission: {
    readonly assignmentSubmissionId: string;
    readonly submittedAt: string;
    readonly fileName: string;
  };
  readonly cleanupWarning: string | null;
}

export interface ResourceRootView {
  readonly scoresEntry: {
    readonly label: '乐谱';
    readonly songCount: number;
  };
  readonly folders: readonly ResourceFolderListItem[];
  readonly files: readonly ResourceFileListItem[];
  readonly canManageOrdinaryResources: boolean;
}

export interface SongDetailView {
  readonly song: SongListItem;
  readonly currentScore: ResourceFileListItem | null;
  readonly relatedResources: {
    readonly folderId: string | null;
    readonly folders: readonly ResourceFolderListItem[];
    readonly files: readonly ResourceFileListItem[];
  };
}

export interface WorkspaceContextInput {
  readonly currentUserId: string;
  readonly currentChoirId: string;
  readonly currentSeasonId: string;
}

export interface AssignmentMemberManagementService {
  readonly listAssignments: (input: {
    readonly workspaceContext: WorkspaceContextInput;
  }) => Promise<readonly AssignmentListItem[]>;
  readonly createAssignment: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly draft: AssignmentDraftInput;
  }) => Promise<AssignmentDetail>;
  readonly getAssignmentDetail: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly assignmentId: string;
  }) => Promise<AssignmentDetail>;
  readonly replaceAssignmentDeliveryRules: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly assignmentId: string;
    readonly deliveryRules: readonly AssignmentDeliveryRuleDraftInput[];
  }) => Promise<AssignmentDetail>;
  readonly createUploadIntent: (input: {
    readonly purpose: 'assignment_submission';
    readonly assignmentId: string;
    readonly fileName: string;
    readonly mimeType: string;
    readonly extension: string;
    readonly sizeBytes: number;
  }) => Promise<AssignmentUploadIntent>;
  readonly uploadAssignmentFile: (input: {
    readonly upload: AssignmentUploadIntent['upload'];
    readonly file: File;
  }) => Promise<void>;
  readonly completeUpload: (input: {
    readonly fileUploadId: string;
    readonly objectKey: string;
    readonly sizeBytes: number;
    readonly etag: string | null;
  }) => Promise<void>;
  readonly submitMyAssignment: (input: {
    readonly assignmentId: string;
    readonly fileUploadId: string;
    readonly note?: string;
  }) => Promise<AssignmentSubmissionResult>;
  readonly getAssignmentSubmissionAccess: (input: { readonly assignmentSubmissionId: string }) => Promise<{
    readonly url: string;
    readonly expiresAt: string;
  }>;
}

export interface MemberManagementViewModel {
  readonly currentUser: UserAccount | null;
  readonly currentChoir: Choir | null;
  readonly currentMembership: Membership | null;
  readonly currentSeason: Season | null;
  readonly isCurrentUserAdmin: boolean;
  readonly currentPermissionKeys: readonly PermissionKey[];
  readonly joinedChoirs: readonly Choir[];
  readonly memberGroups: readonly MemberGroup[];
  readonly memberGroupSummaries: readonly MemberGroupSummary[];
  readonly members: readonly MemberListItem[];
  readonly availableInvitationCodes: readonly InvitationCode[];
  readonly pendingJoinRequests: readonly PendingJoinRequestListItem[];
  readonly ownJoinRequests: readonly JoinRequest[];
  readonly schedules: readonly ScheduleListItem[];
  readonly attendanceCategories: readonly AttendanceCategoryListItem[];
}

export interface MemberManagementService extends AssignmentMemberManagementService {
  readonly identifyEmail: (input: { readonly email: string }) => Promise<EmailIdentification>;
  readonly register: (input: { readonly email: string; readonly password: string }) => Promise<void>;
  readonly login: (input: { readonly email: string; readonly password: string }) => Promise<void>;
  readonly logout: () => Promise<void>;
  readonly createChoir: (input: { readonly name: string }) => Promise<void>;
  readonly switchChoir: (input: { readonly choirId: string }) => Promise<void>;
  readonly listMemberDisplayItems: (input: {
    readonly workspaceContext: WorkspaceContextInput;
  }) => Promise<readonly MemberDisplayListItem[]>;
  readonly listMemberManagementItems: (input: {
    readonly workspaceContext: WorkspaceContextInput;
  }) => Promise<readonly MemberManagementListItem[]>;
  readonly listMemberSelectorItems: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly purpose: MemberSelectorPurpose;
  }) => Promise<readonly MemberSelectorListItem[]>;
  readonly listMemberGroupSummaries: (input: {
    readonly workspaceContext: WorkspaceContextInput;
  }) => Promise<readonly MemberGroupSummary[]>;
  readonly listPermissionGroups: (input: {
    readonly workspaceContext: WorkspaceContextInput;
  }) => Promise<readonly PermissionGroupSummary[]>;
  readonly getPermissionGroupDetail: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly permissionGroupId: string;
  }) => Promise<PermissionGroupDetail>;
  readonly createPermissionGroup: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly name: string;
    readonly permissionKeys: readonly PermissionKey[];
  }) => Promise<PermissionGroupSummary>;
  readonly updatePermissionGroup: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly permissionGroupId: string;
    readonly name: string;
    readonly permissionKeys: readonly PermissionKey[];
  }) => Promise<PermissionGroupSummary>;
  readonly deletePermissionGroup: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly permissionGroupId: string;
  }) => Promise<void>;
  readonly addPermissionGroupMember: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly permissionGroupId: string;
    readonly seasonMembershipId: string;
  }) => Promise<void>;
  readonly removePermissionGroupMember: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly permissionGroupId: string;
    readonly seasonMembershipId: string;
  }) => Promise<void>;
  readonly listInvitations: (input: {
    readonly workspaceContext: WorkspaceContextInput;
  }) => Promise<readonly InvitationListItem[]>;
  readonly listApprovals: (input: {
    readonly workspaceContext: WorkspaceContextInput;
  }) => Promise<readonly ApprovalListItem[]>;
  readonly getApprovalDetail: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly joinRequestId: string;
  }) => Promise<ApprovalDetail>;
  readonly generateInvitationCode: (input: {
    readonly workspaceContext: WorkspaceContextInput;
  }) => Promise<InvitationCode>;
  readonly previewInvitationCode: (input: { readonly code: string }) => Promise<InvitationCodePreview>;
  readonly leaveCurrentChoirWorkspace: () => Promise<void>;
  readonly deleteInvitationCode: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly invitationCodeId: string;
  }) => Promise<InvitationCode>;
  readonly submitJoinRequest: (input: { readonly code: string; readonly nickname: string }) => Promise<JoinRequest>;
  readonly approveJoinRequest: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly joinRequestId: string;
    readonly memberGroupIds: readonly string[];
  }) => Promise<void>;
  readonly rejectJoinRequest: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly joinRequestId: string;
    readonly rejectionReason?: string;
  }) => Promise<void>;
  readonly createMemberGroup: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly name: string;
    readonly initialSeasonMembershipId?: string;
  }) => Promise<MemberGroupSummary>;
  readonly renameMemberGroup: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly memberGroupId: string;
    readonly name: string;
  }) => Promise<void>;
  readonly deleteMemberGroup: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly memberGroupId: string;
  }) => Promise<void>;
  readonly addMemberGroupMember: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly memberGroupId: string;
    readonly seasonMembershipId: string;
  }) => Promise<void>;
  readonly removeMemberGroupMember: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly memberGroupId: string;
    readonly seasonMembershipId: string;
  }) => Promise<void>;
  readonly setMemberGroupManagerGrant: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly memberGroupId: string;
    readonly seasonMembershipId: string;
    readonly enabled: boolean;
  }) => Promise<void>;
  readonly createMemberGroupTag: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly memberGroupId: string;
    readonly name: string;
  }) => Promise<MemberGroupTagListItem>;
  readonly renameMemberGroupTag: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly memberGroupId: string;
    readonly memberGroupTagId: string;
    readonly name: string;
  }) => Promise<void>;
  readonly deleteMemberGroupTag: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly memberGroupId: string;
    readonly memberGroupTagId: string;
  }) => Promise<void>;
  readonly setMemberGroupTagMembership: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly memberGroupId: string;
    readonly memberGroupTagId: string;
    readonly seasonMembershipId: string;
    readonly enabled: boolean;
  }) => Promise<void>;
  readonly getMemberGroupDetail: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly memberGroupId: string;
  }) => Promise<MemberGroupDetail>;
  readonly listSchedules: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    /** UTC calendar date in YYYY-MM-DD format. */
    readonly from: string;
    /** UTC calendar date in YYYY-MM-DD format. */
    readonly to: string;
  }) => Promise<readonly ScheduleListItem[]>;
  readonly listUpcomingSchedules: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly limit: number;
  }) => Promise<readonly ScheduleListItem[]>;
  readonly getScheduleDetail: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly scheduleId: string;
  }) => Promise<ScheduleDetail>;
  readonly createSchedule: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly draft: ScheduleDraftInput;
  }) => Promise<ScheduleDetail>;
  readonly updateSchedule: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly scheduleId: string;
    readonly patch: ScheduleUpdateDraftInput;
  }) => Promise<ScheduleDetail>;
  readonly deleteSchedule: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly scheduleId: string;
  }) => Promise<void>;
  readonly getHomeUpcomingSchedule: (input: {
    readonly workspaceContext: WorkspaceContextInput;
  }) => Promise<ScheduleListItem | null>;
  readonly getScheduleEditOptions: (input: {
    readonly workspaceContext: WorkspaceContextInput;
  }) => Promise<ScheduleEditOptions>;
  readonly listAttendanceCategories: (input: {
    readonly workspaceContext: WorkspaceContextInput;
  }) => Promise<readonly AttendanceCategoryListItem[]>;
  readonly createAttendanceCategory: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly name: string;
  }) => Promise<AttendanceCategoryListItem>;
  readonly renameAttendanceCategory: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly attendanceCategoryId: string;
    readonly name: string;
  }) => Promise<AttendanceCategoryListItem>;
  readonly disableAttendanceCategory: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly attendanceCategoryId: string;
  }) => Promise<AttendanceCategoryListItem>;
  readonly signAttendance: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly scheduleId: string;
    readonly status: 'on_time' | 'late';
  }) => Promise<ScheduleDetail>;
  readonly getResourcesRoot: (input: { readonly workspaceContext: WorkspaceContextInput }) => Promise<ResourceRootView>;
  readonly createResourceFolder: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly parentFolderId: string | null;
    readonly name: string;
  }) => Promise<ResourceFolderListItem>;
  readonly createSong: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly name: string;
  }) => Promise<SongListItem>;
  readonly listSongs: (input: { readonly workspaceContext: WorkspaceContextInput }) => Promise<readonly SongListItem[]>;
  readonly getSongDetail: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly songId: string;
    readonly folderId?: string | null;
  }) => Promise<SongDetailView>;
  readonly createSongRelatedFolder: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly songId: string;
    readonly parentFolderId: string | null;
    readonly name: string;
  }) => Promise<ResourceFolderListItem>;
  readonly createResourceUploadIntent: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly fileName: string;
    readonly mimeType: string;
    readonly sizeBytes: number;
  }) => Promise<{ readonly fileUploadId: string; readonly objectKey: string; readonly upload: UploadAuthorization }>;
  readonly uploadResourceFile: (input: { readonly upload: UploadAuthorization; readonly file: File }) => Promise<void>;
  readonly attachOrdinaryResourceFile: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly parentFolderId: string | null;
    readonly fileUploadId: string;
  }) => Promise<ResourceFileListItem>;
  readonly attachSongRelatedResourceFile: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly songId: string;
    readonly parentFolderId: string | null;
    readonly fileUploadId: string;
  }) => Promise<ResourceFileListItem>;
  readonly updateSongCurrentScore: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly songId: string;
    readonly fileUploadId: string;
    readonly updateNote: string;
  }) => Promise<SongDetailView>;
  readonly getResourceFileAccess: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly resourceFileId: string;
  }) => Promise<{
    readonly url: string;
    readonly expiresAt: string;
    readonly mimeType: string;
    readonly extension: string;
    readonly fileName: string;
  }>;
  readonly readMarkdownResourceFile: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly resourceFileId: string;
  }) => Promise<{ readonly fileName: string; readonly markdown: string }>;
  readonly listNotifications: (input: {
    readonly workspaceContext: WorkspaceContextInput;
  }) => Promise<readonly NotificationListItem[]>;
  readonly getUnreadNotificationState: (input: {
    readonly workspaceContext: WorkspaceContextInput;
  }) => Promise<{ readonly hasUnread: boolean }>;
  readonly markNotificationRead: (input: {
    readonly workspaceContext: WorkspaceContextInput;
    readonly notificationId: string;
  }) => Promise<NotificationListItem | null>;
  readonly getViewModel: () => Promise<MemberManagementViewModel>;
}

export function createMemberManagementService(runtime: MemberManagementRuntime): MemberManagementService {
  const objectStorage = runtime.objectStorage ?? createLocalObjectStoragePort();
  const loadData = async (): Promise<MemberManagementData> => {
    return await runtime.dataStore.load();
  };
  const updateData = async (
    mutator: (current: MemberManagementData) => MaybePromise<MemberManagementData>
  ): Promise<MemberManagementData> => {
    return await runtime.dataStore.update(mutator);
  };
  const resourceRulePorts = {
    now: () => runtime.ports.now(),
    newId: (prefix: string) => runtime.ports.newId(prefix as Parameters<DomainPorts['newId']>[0])
  };

  const requireCurrentUserId = async (): Promise<string> => {
    const userId = await runtime.session.getCurrentUserId();

    if (userId === null) {
      throw new Error('Login is required');
    }

    return userId;
  };

  const requireAdminMembership = async (data: MemberManagementData, userId: string): Promise<Membership> => {
    const currentChoirId = await runtime.session.getCurrentChoirId();
    const membership = data.memberships.find(
      (candidate) => candidate.userId === userId && candidate.choirId === currentChoirId
    );

    if (!membership || membership.role !== 'admin') {
      throw new Error('Choir admin permission is required');
    }

    return membership;
  };

  const requireCurrentMembership = async (data: MemberManagementData, userId: string): Promise<Membership> => {
    const currentChoirId = await runtime.session.getCurrentChoirId();
    const membership = data.memberships.find(
      (candidate) => candidate.userId === userId && candidate.choirId === currentChoirId
    );

    if (!membership) {
      throw new Error('Current choir membership is required');
    }

    return membership;
  };

  const requireWorkspaceMembershipContext = async (
    data: MemberManagementData,
    workspaceContext: WorkspaceContextInput
  ): Promise<Membership> => {
    const currentUserId = await requireCurrentUserId();
    const currentChoirId = await runtime.session.getCurrentChoirId();

    if (currentUserId !== workspaceContext.currentUserId || currentChoirId !== workspaceContext.currentChoirId) {
      throw new Error('Current workspace context is required');
    }

    const currentSeasonId = getCurrentSeasonId(data, workspaceContext.currentChoirId);
    if (currentSeasonId !== workspaceContext.currentSeasonId) {
      throw new Error('Current workspace context is required');
    }

    const currentMembership = data.memberships.find(
      (candidate) =>
        candidate.userId === workspaceContext.currentUserId && candidate.choirId === workspaceContext.currentChoirId
    );

    if (!currentMembership) {
      throw new Error('Current workspace context is required');
    }

    return currentMembership;
  };

  const requireWorkspaceContext = async (
    data: MemberManagementData,
    workspaceContext: WorkspaceContextInput
  ): Promise<{
    readonly currentMembership: Membership;
    readonly currentSeasonMembership: SeasonMembership;
  }> => {
    const currentMembership = await requireWorkspaceMembershipContext(data, workspaceContext);

    return {
      currentMembership,
      currentSeasonMembership: requireCurrentSeasonMembership(data, currentMembership, workspaceContext.currentSeasonId)
    };
  };

  const requireAdminWorkspaceContext = async (
    data: MemberManagementData,
    workspaceContext: WorkspaceContextInput
  ): Promise<{
    readonly actor: Membership;
    readonly currentSeasonId: string;
  }> => {
    const userId = await requireCurrentUserId();
    const actor = await requireAdminMembership(data, userId);
    const currentSeasonId = getCurrentSeasonId(data, actor.choirId);

    if (
      actor.userId !== workspaceContext.currentUserId ||
      actor.choirId !== workspaceContext.currentChoirId ||
      currentSeasonId !== workspaceContext.currentSeasonId
    ) {
      throw new Error('Current workspace context is required');
    }

    return { actor, currentSeasonId };
  };

  const requireMemberApprovalWorkspaceContext = async (
    data: MemberManagementData,
    workspaceContext: WorkspaceContextInput
  ): Promise<{
    readonly actor: Membership;
    readonly currentSeasonId: string;
  }> => {
    const actor = await requireWorkspaceMembershipContext(data, workspaceContext);
    const permissionKeys = resolveCurrentPermissionKeys(data, actor, workspaceContext.currentSeasonId);

    if (!permissionKeys.includes('member_approval')) {
      throw new Error('Member approval permission is required');
    }

    return { actor, currentSeasonId: workspaceContext.currentSeasonId };
  };

  const requirePermissionWorkspaceContext = async (
    data: MemberManagementData,
    workspaceContext: WorkspaceContextInput,
    permissionKey: PermissionKey,
    errorMessage: string
  ): Promise<{
    readonly actor: Membership;
    readonly currentSeasonId: string;
  }> => {
    const actor = await requireWorkspaceMembershipContext(data, workspaceContext);
    const permissionKeys = resolveCurrentPermissionKeys(data, actor, workspaceContext.currentSeasonId);

    if (!permissionKeys.includes(permissionKey)) {
      throw new Error(errorMessage);
    }

    return { actor, currentSeasonId: workspaceContext.currentSeasonId };
  };

  const chooseCurrentChoir = async (data: MemberManagementData, userId: string): Promise<string | null> => {
    const memberships = data.memberships.filter((membership) => membership.userId === userId);
    const currentChoirId = await runtime.session.getCurrentChoirId();

    if (currentChoirId && memberships.some((membership) => membership.choirId === currentChoirId)) {
      return currentChoirId;
    }

    const latestMembership = memberships.reduce<Membership | null>(
      (latest, membership) => (latest === null || membership.createdAt >= latest.createdAt ? membership : latest),
      null
    );

    return latestMembership?.choirId ?? null;
  };

  const settleScheduleReadDataIfNeeded = async (
    data: MemberManagementData,
    scheduleIds: readonly string[],
    now: string
  ): Promise<MemberManagementData> => {
    if (scheduleIds.length === 0) {
      return data;
    }

    if (!needsScheduleReadSettlement(data, scheduleIds, now)) {
      return data;
    }

    return await updateData((current) => {
      if (!needsScheduleReadSettlement(current, scheduleIds, now)) {
        return current;
      }

      const currentSettlement = settleSchedulesIfNeeded(current, scheduleIds, now, runtime.ports);

      return currentSettlement.changed ? currentSettlement.data : current;
    });
  };

  return {
    identifyEmail: async ({ email }) => {
      const normalizedEmail = normalizeEmail(email);
      return {
        email: normalizedEmail,
        status: (await runtime.auth.hasCredentialForEmail(normalizedEmail)) ? 'registered' : 'unregistered'
      };
    },

    register: async ({ email, password }) => {
      const userId = runtime.ports.newId('user');
      const createdAt = runtime.ports.now();

      await runtime.auth.createCredential({
        userId,
        email,
        password
      });

      try {
        const data = await updateData((current) => {
          const result = createUserAccount(current, {
            email,
            userId,
            createdAt
          });
          return result.data;
        });

        await runtime.session.setCurrentUserId(userId);
        await runtime.session.setCurrentChoirId(await chooseCurrentChoir(data, userId));
      } catch (error) {
        const rollbackError = await rollbackRegistration(userId);
        throw rollbackError ?? error;
      }
    },

    login: async ({ email, password }) => {
      const userId = await runtime.auth.verifyCredential(email, password);

      if (userId === null) {
        throw new Error('Email or password is incorrect');
      }

      const data = await loadData();
      if (!data.users.some((user) => user.userId === userId)) {
        await runtime.session.clear();
        throw new Error('Authenticated user is missing business account');
      }

      await runtime.session.setCurrentUserId(userId);
      await runtime.session.setCurrentChoirId(await chooseCurrentChoir(data, userId));
    },

    logout: async () => {
      await runtime.session.clear();
    },

    createChoir: async ({ name }) => {
      const userId = await requireCurrentUserId();
      let choirId: string | null = null;
      await updateData((current) => {
        const result = createChoirInDomain(current, {
          userId,
          name,
          ports: runtime.ports
        });
        const attendanceCategories = buildDefaultAttendanceCategories({
          choirId: result.choir.choirId,
          seasonId: result.season.seasonId,
          createdAt: runtime.ports.now(),
          newId: () => runtime.ports.newId('attendanceCategory')
        });
        choirId = result.choir.choirId;
        return {
          ...result.data,
          attendanceCategories: [...result.data.attendanceCategories, ...attendanceCategories]
        };
      });

      if (choirId === null) {
        throw new Error('Choir was not created');
      }

      await runtime.session.setCurrentChoirId(choirId);
    },

    switchChoir: async ({ choirId }) => {
      const userId = await requireCurrentUserId();
      const data = await loadData();

      if (!getJoinedChoirs(data, userId).some((choir) => choir.choirId === choirId)) {
        throw new Error('Choir is not available to current user');
      }

      await runtime.session.setCurrentChoirId(choirId);
    },

    listMemberDisplayItems: async ({ workspaceContext }) => {
      const data = await loadData();
      await requireWorkspaceContext(data, workspaceContext);

      return getCurrentSeasonMembers(data, workspaceContext.currentChoirId, workspaceContext.currentSeasonId).map(
        toMemberDisplayListItem
      );
    },

    listMemberManagementItems: async ({ workspaceContext }) => {
      const data = await loadData();
      await requireWorkspaceContext(data, workspaceContext);

      return getCurrentSeasonMembers(data, workspaceContext.currentChoirId, workspaceContext.currentSeasonId);
    },

    listMemberSelectorItems: async ({ workspaceContext, purpose }) => {
      assertSupportedMemberSelectorPurpose(purpose);
      const data = await loadData();
      await requireWorkspaceContext(data, workspaceContext);

      return getCurrentSeasonMembers(data, workspaceContext.currentChoirId, workspaceContext.currentSeasonId).map(
        toMemberSelectorListItem
      );
    },

    listMemberGroupSummaries: async ({ workspaceContext }) => {
      const data = await loadData();
      const currentMembership = await requireWorkspaceMembershipContext(data, workspaceContext);

      return data.memberGroups
        .filter(
          (group) =>
            group.choirId === workspaceContext.currentChoirId && group.seasonId === workspaceContext.currentSeasonId
        )
        .sort((left, right) => left.displayOrder - right.displayOrder)
        .map((group) => toMemberGroupSummary(data, group, currentMembership));
    },

    listPermissionGroups: async ({ workspaceContext }) => {
      const data = await loadData();
      const { actor, currentSeasonId } = await requireAdminWorkspaceContext(data, workspaceContext);

      return data.permissionGroups
        .filter((group) => group.choirId === actor.choirId && group.seasonId === currentSeasonId)
        .sort(comparePermissionGroupsByCreatedAt)
        .map((group) => toPermissionGroupSummary(data, group));
    },

    getPermissionGroupDetail: async ({ workspaceContext, permissionGroupId }) => {
      const data = await loadData();
      const { actor, currentSeasonId } = await requireAdminWorkspaceContext(data, workspaceContext);
      const permissionGroup = findPermissionGroupForService(data, permissionGroupId, actor.choirId, currentSeasonId);

      return toPermissionGroupDetail(data, permissionGroup);
    },

    createPermissionGroup: async ({ workspaceContext, name, permissionKeys }) => {
      let permissionGroup: PermissionGroup | null = null;
      const data = await updateData(async (current) => {
        const { actor } = await requireAdminWorkspaceContext(current, workspaceContext);
        const result = createPermissionGroupInDomain(current, {
          actorMembershipId: actor.membershipId,
          name,
          permissionKeys,
          ports: runtime.ports
        });
        permissionGroup = result.permissionGroup;
        return result.data;
      });

      if (permissionGroup === null) {
        throw new Error('Permission group was not created');
      }

      return toPermissionGroupSummary(data, permissionGroup);
    },

    updatePermissionGroup: async ({ workspaceContext, permissionGroupId, name, permissionKeys }) => {
      let permissionGroup: PermissionGroup | null = null;
      const data = await updateData(async (current) => {
        const { actor } = await requireAdminWorkspaceContext(current, workspaceContext);
        const result = updatePermissionGroupInDomain(current, {
          actorMembershipId: actor.membershipId,
          permissionGroupId,
          name,
          permissionKeys,
          ports: runtime.ports
        });
        permissionGroup = result.permissionGroup;
        return result.data;
      });

      if (permissionGroup === null) {
        throw new Error('Permission group was not updated');
      }

      return toPermissionGroupSummary(data, permissionGroup);
    },

    deletePermissionGroup: async ({ workspaceContext, permissionGroupId }) => {
      await updateData(async (current) => {
        const { actor } = await requireAdminWorkspaceContext(current, workspaceContext);
        const result = deletePermissionGroupInDomain(current, {
          actorMembershipId: actor.membershipId,
          permissionGroupId
        });
        return result.data;
      });
    },

    addPermissionGroupMember: async ({ workspaceContext, permissionGroupId, seasonMembershipId }) => {
      await updateData(async (current) => {
        const { actor } = await requireAdminWorkspaceContext(current, workspaceContext);
        const result = addPermissionGroupMemberInDomain(current, {
          actorMembershipId: actor.membershipId,
          permissionGroupId,
          seasonMembershipId,
          ports: runtime.ports
        });
        return result.data;
      });
    },

    removePermissionGroupMember: async ({ workspaceContext, permissionGroupId, seasonMembershipId }) => {
      await updateData(async (current) => {
        const { actor } = await requireAdminWorkspaceContext(current, workspaceContext);
        const result = removePermissionGroupMemberInDomain(current, {
          actorMembershipId: actor.membershipId,
          permissionGroupId,
          seasonMembershipId
        });
        return result.data;
      });
    },

    listInvitations: async ({ workspaceContext }) => {
      const data = await loadData();
      await requireWorkspaceContext(data, workspaceContext);
      const now = runtime.ports.now();

      return data.invitationCodes
        .filter(
          (code) =>
            code.choirId === workspaceContext.currentChoirId &&
            code.seasonId === workspaceContext.currentSeasonId &&
            code.status === 'active' &&
            code.expiresAt > now
        )
        .map(toInvitationListItem);
    },

    listApprovals: async ({ workspaceContext }) => {
      const data = await loadData();
      const { actor, currentSeasonId } = await requireMemberApprovalWorkspaceContext(data, workspaceContext);

      return data.joinRequests
        .filter(
          (request) =>
            request.choirId === actor.choirId && request.seasonId === currentSeasonId && request.status === 'pending'
        )
        .map((request) => toApprovalListItem(data, request));
    },

    getApprovalDetail: async ({ workspaceContext, joinRequestId }) => {
      const data = await loadData();
      const { actor, currentSeasonId } = await requireMemberApprovalWorkspaceContext(data, workspaceContext);
      const joinRequest = requirePendingApprovalJoinRequestForWorkspace(data, {
        joinRequestId,
        choirId: actor.choirId,
        seasonId: currentSeasonId
      });

      return toApprovalDetail(data, joinRequest);
    },

    generateInvitationCode: async ({ workspaceContext }) => {
      for (let attempt = 1; attempt <= MAX_INVITATION_CODE_GENERATION_ATTEMPTS; attempt += 1) {
        let invitationCode: InvitationCode | null = null;

        try {
          await updateData(async (current) => {
            const { currentMembership: actor } = await requireWorkspaceContext(current, workspaceContext);
            const code = await runtime.invitationCodeGenerator.newInvitationCode();
            const result = generateInvitationCodeInDomain(current, {
              actorMembershipId: actor.membershipId,
              code,
              ports: runtime.ports
            });
            invitationCode = result.invitationCode;
            return result.data;
          });
        } catch (error) {
          if (!isInvitationCodeCollision(error)) {
            throw error;
          }

          if (attempt === MAX_INVITATION_CODE_GENERATION_ATTEMPTS) {
            throw new Error('Unable to generate unique invitation code', { cause: error });
          }

          continue;
        }

        if (invitationCode === null) {
          throw new Error('Invitation code was not generated');
        }

        return invitationCode;
      }

      throw new Error('Unable to generate unique invitation code');
    },

    previewInvitationCode: async ({ code }) => {
      await requireCurrentUserId();
      const data = await loadData();
      const invitationCode = data.invitationCodes.find((candidate) => candidate.code === code.trim());

      if (!invitationCode) {
        throw new Error('Invitation code not found');
      }

      const now = runtime.ports.now();

      const usabilityProblem = getInvitationCodeUsabilityProblem(invitationCode, now);

      if (usabilityProblem === 'deleted') {
        throw new Error('Invitation code is deleted');
      }

      if (usabilityProblem === 'expired') {
        throw new Error('Invitation code is expired');
      }

      const choir = data.choirs.find((candidate) => candidate.choirId === invitationCode.choirId);
      const season = data.seasons.find((candidate) => candidate.seasonId === invitationCode.seasonId);

      if (!choir) {
        throw new Error('Choir not found');
      }

      if (!season) {
        throw new Error('Season not found');
      }

      return {
        code: invitationCode.code,
        choirId: choir.choirId,
        choirName: choir.name,
        seasonId: season.seasonId,
        seasonName: season.name
      };
    },

    leaveCurrentChoirWorkspace: async () => {
      await requireCurrentUserId();
      await runtime.session.setCurrentChoirId(null);
    },

    deleteInvitationCode: async ({ workspaceContext, invitationCodeId }) => {
      let invitationCode: InvitationCode | null = null;
      await updateData(async (current) => {
        const { currentMembership: actor } = await requireWorkspaceContext(current, workspaceContext);
        const result = deleteInvitationCodeInDomain(current, {
          actorMembershipId: actor.membershipId,
          invitationCodeId,
          ports: runtime.ports
        });
        invitationCode = result.invitationCode;
        return result.data;
      });

      if (invitationCode === null) {
        throw new Error('Invitation code was not deleted');
      }

      return invitationCode;
    },

    submitJoinRequest: async ({ code, nickname }) => {
      const userId = await requireCurrentUserId();
      let joinRequest: JoinRequest | null = null;
      await updateData((current) => {
        const result = submitJoinRequestInDomain(current, {
          userId,
          code,
          nickname,
          ports: runtime.ports
        });
        joinRequest = result.joinRequest;
        return result.data;
      });

      if (joinRequest === null) {
        throw new Error('Join request was not submitted');
      }

      return joinRequest;
    },

    approveJoinRequest: async ({ workspaceContext, joinRequestId, memberGroupIds }) => {
      await updateData(async (current) => {
        const { actor, currentSeasonId } = await requireMemberApprovalWorkspaceContext(current, workspaceContext);
        requirePendingApprovalJoinRequestForWorkspace(current, {
          joinRequestId,
          choirId: actor.choirId,
          seasonId: currentSeasonId
        });
        const result = approveJoinRequestInDomain(current, {
          actorMembershipId: actor.membershipId,
          joinRequestId,
          memberGroupIds,
          ports: runtime.ports
        });
        return result.data;
      });
    },

    rejectJoinRequest: async ({ workspaceContext, joinRequestId, rejectionReason }) => {
      await updateData(async (current) => {
        const { actor, currentSeasonId } = await requireMemberApprovalWorkspaceContext(current, workspaceContext);
        requirePendingApprovalJoinRequestForWorkspace(current, {
          joinRequestId,
          choirId: actor.choirId,
          seasonId: currentSeasonId
        });
        const result = rejectJoinRequestInDomain(current, {
          actorMembershipId: actor.membershipId,
          joinRequestId,
          rejectionReason,
          ports: runtime.ports
        });
        return result.data;
      });
    },

    createMemberGroup: async ({ workspaceContext, name, initialSeasonMembershipId }) => {
      let memberGroup: MemberGroup | null = null;
      const data = await updateData(async (current) => {
        const actor = await requireWorkspaceMembershipContext(current, workspaceContext);
        const created = createMemberGroupInDomain(current, {
          actorMembershipId: actor.membershipId,
          name,
          ports: runtime.ports
        });
        memberGroup = created.memberGroup;

        if (!initialSeasonMembershipId) {
          return created.data;
        }

        return addSeasonMembershipToMemberGroupInDomain(created.data, {
          actorMembershipId: actor.membershipId,
          memberGroupId: created.memberGroup.memberGroupId,
          seasonMembershipId: initialSeasonMembershipId,
          ports: runtime.ports
        }).data;
      });

      if (memberGroup === null) {
        throw new Error('Member group was not created');
      }

      const { currentMembership } = await requireWorkspaceContext(data, workspaceContext);
      return toMemberGroupSummary(data, memberGroup, currentMembership);
    },

    renameMemberGroup: async ({ workspaceContext, memberGroupId, name }) => {
      await updateData(async (current) => {
        const actor = await requireWorkspaceMembershipContext(current, workspaceContext);
        const result = renameMemberGroupInDomain(current, {
          actorMembershipId: actor.membershipId,
          memberGroupId,
          name
        });
        return result.data;
      });
    },

    deleteMemberGroup: async ({ workspaceContext, memberGroupId }) => {
      await updateData(async (current) => {
        const actor = await requireWorkspaceMembershipContext(current, workspaceContext);
        const result = deleteMemberGroupInDomain(current, {
          actorMembershipId: actor.membershipId,
          memberGroupId
        });
        return result.data;
      });
    },

    addMemberGroupMember: async ({ workspaceContext, memberGroupId, seasonMembershipId }) => {
      await updateData(async (current) => {
        const actor = await requireWorkspaceMembershipContext(current, workspaceContext);
        const result = addSeasonMembershipToMemberGroupInDomain(current, {
          actorMembershipId: actor.membershipId,
          memberGroupId,
          seasonMembershipId,
          ports: runtime.ports
        });
        return result.data;
      });
    },

    removeMemberGroupMember: async ({ workspaceContext, memberGroupId, seasonMembershipId }) => {
      await updateData(async (current) => {
        const actor = await requireWorkspaceMembershipContext(current, workspaceContext);
        const result = removeSeasonMembershipFromMemberGroupInDomain(current, {
          actorMembershipId: actor.membershipId,
          memberGroupId,
          seasonMembershipId
        });
        return result.data;
      });
    },

    setMemberGroupManagerGrant: async ({ workspaceContext, memberGroupId, seasonMembershipId, enabled }) => {
      await updateData(async (current) => {
        const actor = await requireWorkspaceMembershipContext(current, workspaceContext);
        const result = setMemberGroupManagerGrantInDomain(current, {
          actorMembershipId: actor.membershipId,
          memberGroupId,
          seasonMembershipId,
          enabled,
          ports: runtime.ports
        });
        return result.data;
      });
    },

    createMemberGroupTag: async ({ workspaceContext, memberGroupId, name }) => {
      let memberGroupTag: MemberGroupTag | null = null;
      const data = await updateData(async (current) => {
        const actor = await requireWorkspaceMembershipContext(current, workspaceContext);
        const result = createMemberGroupTagInDomain(current, {
          actorMembershipId: actor.membershipId,
          memberGroupId,
          name,
          ports: runtime.ports
        });
        memberGroupTag = result.memberGroupTag;
        return result.data;
      });

      if (memberGroupTag === null) {
        throw new Error('Member group tag was not created');
      }

      return toMemberGroupTagListItem(data, memberGroupTag);
    },

    renameMemberGroupTag: async ({ workspaceContext, memberGroupId, memberGroupTagId, name }) => {
      await updateData(async (current) => {
        const actor = await requireWorkspaceMembershipContext(current, workspaceContext);
        requireMemberGroupTagBelongsToGroup(current, memberGroupId, memberGroupTagId);
        const result = renameMemberGroupTagInDomain(current, {
          actorMembershipId: actor.membershipId,
          memberGroupTagId,
          name
        });
        return result.data;
      });
    },

    deleteMemberGroupTag: async ({ workspaceContext, memberGroupId, memberGroupTagId }) => {
      await updateData(async (current) => {
        const actor = await requireWorkspaceMembershipContext(current, workspaceContext);
        requireMemberGroupTagBelongsToGroup(current, memberGroupId, memberGroupTagId);
        const result = deleteMemberGroupTagInDomain(current, {
          actorMembershipId: actor.membershipId,
          memberGroupTagId
        });
        return result.data;
      });
    },

    setMemberGroupTagMembership: async ({
      workspaceContext,
      memberGroupId,
      memberGroupTagId,
      seasonMembershipId,
      enabled
    }) => {
      await updateData(async (current) => {
        const actor = await requireWorkspaceMembershipContext(current, workspaceContext);
        requireMemberGroupTagBelongsToGroup(current, memberGroupId, memberGroupTagId);
        const result = setMemberGroupTagMembershipInDomain(current, {
          actorMembershipId: actor.membershipId,
          memberGroupTagId,
          seasonMembershipId,
          enabled
        });
        return result.data;
      });
    },

    getMemberGroupDetail: async ({ workspaceContext, memberGroupId }) => {
      const data = await loadData();
      const currentMembership = await requireWorkspaceMembershipContext(data, workspaceContext);
      const memberGroup = findMemberGroupForService(data, memberGroupId, currentMembership);

      return toMemberGroupDetail(data, memberGroup, currentMembership);
    },

    listSchedules: async ({ workspaceContext, from, to }) => {
      const now = runtime.ports.now();
      let data = await loadData();
      let { currentMembership } = await requireWorkspaceContext(data, workspaceContext);
      const scheduleIds = findActiveSchedulesInWindow(
        data,
        workspaceContext.currentChoirId,
        workspaceContext.currentSeasonId,
        from,
        to
      ).map((schedule) => schedule.scheduleId);
      data = await settleScheduleReadDataIfNeeded(data, scheduleIds, now);
      ({ currentMembership } = await requireWorkspaceContext(data, workspaceContext));

      return findActiveSchedulesInWindow(
        data,
        workspaceContext.currentChoirId,
        workspaceContext.currentSeasonId,
        from,
        to
      ).map((schedule) => toScheduleListItem(data, schedule, currentMembership, now));
    },

    listUpcomingSchedules: async ({ workspaceContext, limit }) => {
      const normalizedLimit = normalizeScheduleListLimit(limit);
      const now = runtime.ports.now();
      let data = await loadData();
      let { currentMembership } = await requireWorkspaceContext(data, workspaceContext);
      const scheduleIds = data.schedules
        .filter(
          (schedule) =>
            schedule.choirId === workspaceContext.currentChoirId &&
            schedule.seasonId === workspaceContext.currentSeasonId &&
            schedule.status === 'active'
        )
        .map((schedule) => schedule.scheduleId);
      data = await settleScheduleReadDataIfNeeded(data, scheduleIds, now);
      ({ currentMembership } = await requireWorkspaceContext(data, workspaceContext));
      const schedules = data.schedules
        .filter(
          (schedule) =>
            schedule.choirId === workspaceContext.currentChoirId &&
            schedule.seasonId === workspaceContext.currentSeasonId &&
            schedule.status === 'active'
        )
        .sort(compareSchedulesByStart)
        .map((schedule) => toScheduleListItem(data, schedule, currentMembership, now));

      return selectUpcomingSchedulesFromSortedList(schedules, now, normalizedLimit);
    },

    getScheduleDetail: async ({ workspaceContext, scheduleId }) => {
      const now = runtime.ports.now();
      let data = await loadData();
      let { currentMembership } = await requireWorkspaceContext(data, workspaceContext);
      findScheduleForService(data, scheduleId, currentMembership, workspaceContext.currentSeasonId);
      data = await settleScheduleReadDataIfNeeded(data, [scheduleId], now);
      ({ currentMembership } = await requireWorkspaceContext(data, workspaceContext));
      const schedule = findScheduleForService(data, scheduleId, currentMembership, workspaceContext.currentSeasonId);

      return toScheduleDetail(data, schedule, currentMembership, now);
    },

    createSchedule: async ({ workspaceContext, draft }) => {
      assertScheduleAttendanceIntentExplicit(draft);
      let scheduleId: string | null = null;
      const data = await updateData(async (current) => {
        const { actor, currentSeasonId } = await requirePermissionWorkspaceContext(
          current,
          workspaceContext,
          'schedule_manage',
          'Schedule management permission is required'
        );
        const schedule = createScheduleEntities({
          choirId: actor.choirId,
          seasonId: currentSeasonId,
          actorMembershipId: actor.membershipId,
          now: runtime.ports.now(),
          scheduleId: runtime.ports.newId('schedule'),
          draft: normalizeScheduleDraft(draft),
          attendance: draft.attendance
            ? {
                attendanceEventId: runtime.ports.newId('attendanceEvent'),
                ...validateScheduleAttendanceDraft(current, actor.choirId, currentSeasonId, draft.attendance)
              }
            : null
        });
        scheduleId = schedule.schedule.scheduleId;

        return {
          ...current,
          schedules: [...current.schedules, schedule.schedule],
          attendanceEvents: schedule.attendanceEvent
            ? [...current.attendanceEvents, schedule.attendanceEvent]
            : current.attendanceEvents,
          attendanceEventMemberGroups: [...current.attendanceEventMemberGroups, ...schedule.attendanceEventMemberGroups]
        };
      });
      const { currentMembership } = await requireWorkspaceContext(data, workspaceContext);

      if (scheduleId === null) {
        throw new Error('Schedule was not created');
      }

      return toScheduleDetail(
        data,
        findScheduleForService(data, scheduleId, currentMembership, workspaceContext.currentSeasonId),
        currentMembership,
        runtime.ports.now()
      );
    },

    updateSchedule: async ({ workspaceContext, scheduleId, patch }) => {
      const data = await updateData(async (current) => {
        const { actor, currentSeasonId } = await requirePermissionWorkspaceContext(
          current,
          workspaceContext,
          'schedule_manage',
          'Schedule management permission is required'
        );
        const existing = findScheduleForService(current, scheduleId, actor, currentSeasonId);
        const existingAttendanceEvent = findAttendanceEventForSchedule(current, scheduleId);
        const now = runtime.ports.now();
        assertStartedSchedulePatchDoesNotCarryLockedFields(existing, patch, now);
        const mergedDraft = mergeScheduleUpdateDraft(existing, patch);
        const update = applyScheduleUpdate({
          existing,
          existingAttendanceEvent,
          now,
          draft: normalizeScheduleDraft(mergedDraft),
          attendance:
            patch.attendance === undefined
              ? undefined
              : patch.attendance
                ? validateScheduleAttendanceDraft(current, actor.choirId, currentSeasonId, patch.attendance)
                : null
        });
        const nextData = {
          ...current,
          schedules: replaceByScheduleId(current.schedules, update.schedule)
        };

        if (!update.canReplaceAttendanceSettings || patch.attendance === undefined) {
          return nextData;
        }

        return replaceScheduleAttendanceDraft(
          nextData,
          update.schedule,
          existingAttendanceEvent,
          patch.attendance,
          now,
          runtime.ports
        );
      });
      const { currentMembership } = await requireWorkspaceContext(data, workspaceContext);

      return toScheduleDetail(
        data,
        findScheduleForService(data, scheduleId, currentMembership, workspaceContext.currentSeasonId),
        currentMembership,
        runtime.ports.now()
      );
    },

    deleteSchedule: async ({ workspaceContext, scheduleId }) => {
      await updateData(async (current) => {
        const { actor, currentSeasonId } = await requirePermissionWorkspaceContext(
          current,
          workspaceContext,
          'schedule_manage',
          'Schedule management permission is required'
        );
        const existing = findScheduleForService(current, scheduleId, actor, currentSeasonId);

        return {
          ...current,
          schedules: replaceByScheduleId(
            current.schedules,
            markScheduleDeleted({
              existing,
              now: runtime.ports.now()
            })
          )
        };
      });
    },

    getHomeUpcomingSchedule: async ({ workspaceContext }) => {
      const data = await loadData();
      const { currentMembership } = await requireWorkspaceContext(data, workspaceContext);
      const now = runtime.ports.now();
      const scheduleIds = data.schedules
        .filter(
          (schedule) =>
            schedule.choirId === workspaceContext.currentChoirId &&
            schedule.seasonId === workspaceContext.currentSeasonId &&
            schedule.status === 'active'
        )
        .map((schedule) => schedule.scheduleId);
      const settled = await settleScheduleReadDataIfNeeded(data, scheduleIds, now);
      const activeSchedules = settled.schedules
        .filter(
          (schedule) =>
            schedule.choirId === workspaceContext.currentChoirId &&
            schedule.seasonId === workspaceContext.currentSeasonId &&
            schedule.status === 'active'
        )
        .sort(compareSchedulesByStart)
        .map((schedule) => toScheduleListItem(settled, schedule, currentMembership, now));

      return selectHomeScheduleFromSortedList(activeSchedules, now);
    },

    getScheduleEditOptions: async ({ workspaceContext }) => {
      const data = await loadData();
      await requireWorkspaceContext(data, workspaceContext);

      return {
        attendanceCategories: findAttendanceCategoriesForSeason(
          data,
          workspaceContext.currentChoirId,
          workspaceContext.currentSeasonId
        ).filter((category) => category.status === 'enabled'),
        participantMemberGroups: data.memberGroups
          .filter(
            (group) =>
              group.choirId === workspaceContext.currentChoirId && group.seasonId === workspaceContext.currentSeasonId
          )
          .sort((left, right) => left.displayOrder - right.displayOrder)
          .map(toScheduleParticipantMemberGroupOption)
      };
    },

    listAttendanceCategories: async ({ workspaceContext }) => {
      const data = await loadData();
      await requireWorkspaceContext(data, workspaceContext);

      return findAttendanceCategoriesForSeason(data, workspaceContext.currentChoirId, workspaceContext.currentSeasonId);
    },

    createAttendanceCategory: async ({ workspaceContext, name }) => {
      let category: AttendanceCategory | null = null;
      await updateData(async (current) => {
        const { actor, currentSeasonId: seasonId } = await requireAdminWorkspaceContext(current, workspaceContext);
        const seasonCategories = findAttendanceCategoriesForSeason(current, actor.choirId, seasonId);
        const normalizedName = normalizeRequiredText(name, 'Attendance category name');

        if (hasEnabledAttendanceCategoryNameCollision(seasonCategories, normalizedName)) {
          throw new Error('Attendance category name already exists in this season');
        }

        const now = runtime.ports.now();
        category = {
          attendanceCategoryId: runtime.ports.newId('attendanceCategory'),
          choirId: actor.choirId,
          seasonId,
          name: normalizedName,
          status: 'enabled',
          displayOrder: nextDisplayOrder(seasonCategories),
          createdAt: now,
          updatedAt: now
        };

        return {
          ...current,
          attendanceCategories: [...current.attendanceCategories, category]
        };
      });

      if (category === null) {
        throw new Error('Attendance category was not created');
      }

      return category;
    },

    renameAttendanceCategory: async ({ workspaceContext, attendanceCategoryId, name }) => {
      let category: AttendanceCategory | null = null;
      await updateData(async (current) => {
        const { actor, currentSeasonId: seasonId } = await requireAdminWorkspaceContext(current, workspaceContext);
        const existing = findAttendanceCategoryForService(current, attendanceCategoryId, actor, seasonId);
        const seasonCategories = findAttendanceCategoriesForSeason(current, actor.choirId, seasonId);
        const normalizedName = normalizeRequiredText(name, 'Attendance category name');

        if (
          existing.status === 'enabled' &&
          hasEnabledAttendanceCategoryNameCollision(seasonCategories, normalizedName, existing.attendanceCategoryId)
        ) {
          throw new Error('Attendance category name already exists in this season');
        }

        category = {
          ...existing,
          name: normalizedName,
          updatedAt: runtime.ports.now()
        };

        return {
          ...current,
          attendanceCategories: replaceByAttendanceCategoryId(current.attendanceCategories, category)
        };
      });

      if (category === null) {
        throw new Error('Attendance category was not renamed');
      }

      return category;
    },

    disableAttendanceCategory: async ({ workspaceContext, attendanceCategoryId }) => {
      let category: AttendanceCategory | null = null;
      await updateData(async (current) => {
        const { actor, currentSeasonId: seasonId } = await requireAdminWorkspaceContext(current, workspaceContext);
        const existing = findAttendanceCategoryForService(current, attendanceCategoryId, actor, seasonId);
        category = {
          ...existing,
          status: 'disabled',
          updatedAt: runtime.ports.now()
        };

        return {
          ...current,
          attendanceCategories: replaceByAttendanceCategoryId(current.attendanceCategories, category)
        };
      });

      if (category === null) {
        throw new Error('Attendance category was not disabled');
      }

      return category;
    },

    signAttendance: async ({ workspaceContext, scheduleId, status }) => {
      const now = runtime.ports.now();
      const data = await updateData(async (current) => {
        const { currentMembership } = await requireWorkspaceContext(current, workspaceContext);
        const settled = settleSchedulesIfNeeded(current, [scheduleId], now, runtime.ports).data;
        const schedule = findScheduleForService(
          settled,
          scheduleId,
          currentMembership,
          workspaceContext.currentSeasonId
        );
        const attendanceEvent = findAttendanceEventForSchedule(settled, scheduleId);

        if (!attendanceEvent) {
          throw new Error('Schedule attendance is not enabled');
        }

        const currentSeasonMembership = findActiveSeasonMembershipForSchedule(settled, currentMembership, schedule);
        const record = currentSeasonMembership
          ? settled.attendanceRecords.find(
              (candidate) =>
                candidate.attendanceEventId === attendanceEvent.attendanceEventId &&
                candidate.seasonMembershipId === currentSeasonMembership.seasonMembershipId
            )
          : null;
        const signedRecord = signAttendanceRecord({
          schedule,
          attendanceEvent,
          record,
          now,
          status
        });

        return {
          ...settled,
          attendanceRecords: replaceByAttendanceRecordId(settled.attendanceRecords, signedRecord)
        };
      });
      const { currentMembership } = await requireWorkspaceContext(data, workspaceContext);

      return toScheduleDetail(
        data,
        findScheduleForService(data, scheduleId, currentMembership, workspaceContext.currentSeasonId),
        currentMembership,
        now
      );
    },

    listAssignments: async ({ workspaceContext }) => {
      const data = await loadData();
      const { currentMembership, currentSeasonMembership } = await requireWorkspaceContext(data, workspaceContext);
      const now = runtime.ports.now();

      return data.assignments
        .filter(
          (assignment) =>
            assignment.choirId === workspaceContext.currentChoirId &&
            assignment.seasonId === workspaceContext.currentSeasonId &&
            assignment.status === 'published'
        )
        .filter((assignment) => canViewAssignment(data, assignment, currentMembership, currentSeasonMembership))
        .map((assignment) =>
          projectAssignmentListItem({
            assignment,
            currentSeasonMembershipId: currentSeasonMembership.seasonMembershipId,
            canManage: canManageAssignment(data, assignment, currentMembership, workspaceContext.currentSeasonId),
            nowIso: now,
            resolvedDeliveryRules: resolveAssignmentDeliveryRuleViews(data, assignment),
            submissions: data.assignmentSubmissions
          })
        );
    },

    createAssignment: async ({ workspaceContext, draft }) => {
      const userId = await requireCurrentUserId();
      let assignmentId: string | null = null;
      const data = await updateData(async (current) => {
        const { actor, currentSeasonId } = await requirePermissionWorkspaceContext(
          current,
          workspaceContext,
          'assignment_publish_manage',
          'Assignment publish management permission is required'
        );
        const normalized = normalizeAssignmentDraft(draft);
        const now = runtime.ports.now();
        const assignment: Assignment = {
          assignmentId: runtime.ports.newId('assignment'),
          choirId: actor.choirId,
          seasonId: currentSeasonId,
          pieceName: normalized.pieceName,
          dueAt: normalized.dueAt,
          submissionType: normalized.submissionType,
          description: normalized.description,
          status: 'published',
          createdByMembershipId: actor.membershipId,
          createdAt: now,
          updatedAt: now,
          deletedAt: null
        };
        const deliveryRows = buildAssignmentDeliveryRows(assignment.assignmentId, draft.deliveryRules, runtime.ports);
        assertResolvableDeliveryRows(current, assignment, deliveryRows);
        assignmentId = assignment.assignmentId;

        return {
          ...current,
          assignments: [...current.assignments, assignment],
          assignmentDeliveryRules: [...current.assignmentDeliveryRules, ...deliveryRows.rules],
          assignmentDeliveryRuleTargets: [...current.assignmentDeliveryRuleTargets, ...deliveryRows.targets],
          assignmentDeliveryRuleReviewers: [...current.assignmentDeliveryRuleReviewers, ...deliveryRows.reviewers]
        };
      });
      const currentMembership = await requireCurrentMembership(data, userId);

      if (assignmentId === null) {
        throw new Error('Assignment was not created');
      }

      return toAssignmentDetailForService(data, assignmentId, currentMembership, runtime.ports.now());
    },

    getAssignmentDetail: async ({ workspaceContext, assignmentId }) => {
      const data = await loadData();
      const { currentMembership } = await requireWorkspaceContext(data, workspaceContext);

      return toAssignmentDetailForService(data, assignmentId, currentMembership, runtime.ports.now());
    },

    replaceAssignmentDeliveryRules: async ({ workspaceContext, assignmentId, deliveryRules }) => {
      const userId = await requireCurrentUserId();
      const data = await updateData(async (current) => {
        const { actor, currentSeasonId } = await requirePermissionWorkspaceContext(
          current,
          workspaceContext,
          'assignment_publish_manage',
          'Assignment publish management permission is required'
        );
        const assignment = findAssignmentForService(current, assignmentId, actor, currentSeasonId);
        if (!canManageAssignment(current, assignment, actor, currentSeasonId)) {
          throw new Error('Assignment publish management permission is required');
        }
        const deliveryRows = buildAssignmentDeliveryRows(assignment.assignmentId, deliveryRules, runtime.ports);
        assertResolvableDeliveryRows(current, assignment, deliveryRows);
        const replacedRuleIds = new Set(
          current.assignmentDeliveryRules
            .filter((rule) => rule.assignmentId === assignment.assignmentId)
            .map((rule) => rule.assignmentDeliveryRuleId)
        );

        return {
          ...current,
          assignmentDeliveryRules: [
            ...current.assignmentDeliveryRules.filter((rule) => rule.assignmentId !== assignment.assignmentId),
            ...deliveryRows.rules
          ],
          assignmentDeliveryRuleTargets: [
            ...current.assignmentDeliveryRuleTargets.filter(
              (target) => !replacedRuleIds.has(target.assignmentDeliveryRuleId)
            ),
            ...deliveryRows.targets
          ],
          assignmentDeliveryRuleReviewers: [
            ...current.assignmentDeliveryRuleReviewers.filter(
              (reviewer) => !replacedRuleIds.has(reviewer.assignmentDeliveryRuleId)
            ),
            ...deliveryRows.reviewers
          ]
        };
      });
      const currentMembership = await requireCurrentMembership(data, userId);

      return toAssignmentDetailForService(data, assignmentId, currentMembership, runtime.ports.now());
    },

    createUploadIntent: async ({ purpose, assignmentId, fileName, mimeType, extension, sizeBytes }) => {
      const userId = await requireCurrentUserId();
      if (purpose !== 'assignment_submission') {
        throw new Error('Upload purpose is invalid');
      }

      const data = await loadData();
      const currentMembership = await requireCurrentMembership(data, userId);
      const currentSeasonId = getCurrentSeasonId(data, currentMembership.choirId);
      const currentSeasonMembership = requireCurrentSeasonMembership(data, currentMembership, currentSeasonId);
      const assignment = findAssignmentForService(data, assignmentId, currentMembership, currentSeasonId);
      assertCurrentMemberCanSubmitAssignment(data, assignment, currentSeasonMembership);
      assertCanSubmitAssignment({ nowIso: runtime.ports.now(), dueAt: assignment.dueAt });
      const normalizedFileName = fileName.trim();
      const normalizedMimeType = mimeType.trim().toLowerCase();
      const normalizedExtension = normalizeUploadExtension(extension, normalizedFileName);
      validateAssignmentSubmissionFile({
        submissionType: assignment.submissionType,
        fileName: normalizedFileName,
        mimeType: normalizedMimeType,
        sizeBytes
      });

      const fileUploadId = runtime.ports.newId('fileUpload');
      const objectKey = `assignments/${assignment.assignmentId}/${currentSeasonMembership.seasonMembershipId}/${fileUploadId}.${normalizedExtension}`;
      const upload = await objectStorage.createUploadAuthorization({
        objectKey,
        mimeType: normalizedMimeType,
        sizeBytes
      });
      const createdAt = runtime.ports.now();

      await updateData((current) => ({
        ...current,
        fileUploads: [
          ...current.fileUploads,
          {
            fileUploadId,
            purpose: 'assignment_submission',
            status: 'pending',
            choirId: assignment.choirId,
            seasonId: assignment.seasonId,
            assignmentId: assignment.assignmentId,
            createdByUserId: userId,
            createdBySeasonMembershipId: currentSeasonMembership.seasonMembershipId,
            objectKey,
            fileName: normalizedFileName,
            mimeType: normalizedMimeType,
            extension: normalizedExtension,
            sizeBytes,
            etag: null,
            expiresAt: upload.expiresAt,
            createdAt,
            completedAt: null,
            attachedAt: null
          }
        ]
      }));

      return {
        fileUploadId,
        objectKey,
        upload
      };
    },

    uploadAssignmentFile: async ({ upload, file }) => {
      await objectStorage.uploadObject({ upload, file });
    },

    completeUpload: async ({ fileUploadId, objectKey, sizeBytes, etag }) => {
      const userId = await requireCurrentUserId();
      await updateData(async (current) => {
        const currentMembership = await requireCurrentMembership(current, userId);
        const currentSeasonId = getCurrentSeasonId(current, currentMembership.choirId);
        const currentSeasonMembership = requireCurrentSeasonMembership(current, currentMembership, currentSeasonId);
        const upload = current.fileUploads.find((candidate) => candidate.fileUploadId === fileUploadId);

        if (!upload) {
          throw new Error('File upload not found');
        }

        if (
          upload.status !== 'pending' ||
          upload.createdByUserId !== userId ||
          upload.createdBySeasonMembershipId !== currentSeasonMembership.seasonMembershipId ||
          upload.objectKey !== objectKey ||
          upload.sizeBytes !== sizeBytes
        ) {
          throw new Error('File upload completion does not match upload intent');
        }

        return {
          ...current,
          fileUploads: current.fileUploads.map((candidate) =>
            candidate.fileUploadId === fileUploadId
              ? {
                  ...candidate,
                  status: 'completed',
                  etag,
                  completedAt: runtime.ports.now()
                }
              : candidate
          )
        };
      });
    },

    submitMyAssignment: async ({ assignmentId, fileUploadId, note }) => {
      const userId = await requireCurrentUserId();
      let oldObjectKeyToDelete: string | null = null;
      const data = await updateData(async (current) => {
        const currentMembership = await requireCurrentMembership(current, userId);
        const currentSeasonId = getCurrentSeasonId(current, currentMembership.choirId);
        const currentSeasonMembership = requireCurrentSeasonMembership(current, currentMembership, currentSeasonId);
        const assignment = findAssignmentForService(current, assignmentId, currentMembership, currentSeasonId);
        assertCurrentMemberCanSubmitAssignment(current, assignment, currentSeasonMembership);
        assertCanSubmitAssignment({ nowIso: runtime.ports.now(), dueAt: assignment.dueAt });
        const upload = current.fileUploads.find((candidate) => candidate.fileUploadId === fileUploadId);

        if (!upload) {
          throw new Error('File upload not found');
        }

        if (
          upload.status !== 'completed' ||
          upload.purpose !== 'assignment_submission' ||
          upload.assignmentId !== assignment.assignmentId ||
          upload.choirId !== assignment.choirId ||
          upload.seasonId !== assignment.seasonId ||
          upload.createdByUserId !== userId ||
          upload.createdBySeasonMembershipId !== currentSeasonMembership.seasonMembershipId
        ) {
          throw new Error('Completed assignment upload does not match current member');
        }

        const existingSubmission =
          current.assignmentSubmissions.find(
            (submission) =>
              submission.assignmentId === assignment.assignmentId &&
              submission.choirId === assignment.choirId &&
              submission.seasonId === assignment.seasonId &&
              submission.submitterSeasonMembershipId === currentSeasonMembership.seasonMembershipId
          ) ?? null;
        const applied = applyAssignmentSubmission({
          existingSubmission,
          completedUpload: {
            ...upload,
            status: 'completed',
            assignmentId: assignment.assignmentId
          },
          note: note ?? '',
          nowIso: runtime.ports.now(),
          newSubmissionId: () => runtime.ports.newId('assignmentSubmission')
        });
        oldObjectKeyToDelete =
          applied.oldObjectKeyToDelete !== null && applied.oldObjectKeyToDelete !== applied.submission.objectKey
            ? applied.oldObjectKeyToDelete
            : null;

        return {
          ...current,
          assignmentSubmissions: [
            ...current.assignmentSubmissions.filter(
              (submission) =>
                !(
                  submission.assignmentId === assignment.assignmentId &&
                  submission.choirId === assignment.choirId &&
                  submission.seasonId === assignment.seasonId &&
                  submission.submitterSeasonMembershipId === currentSeasonMembership.seasonMembershipId
                )
            ),
            applied.submission
          ]
        };
      });

      const submissionForResult = data.assignmentSubmissions.find(
        (submission) => submission.assignmentId === assignmentId && submission.fileUploadId === fileUploadId
      );

      if (!submissionForResult) {
        throw new Error('Assignment submission was not saved');
      }

      let cleanupWarning: string | null = null;
      if (oldObjectKeyToDelete !== null) {
        const cleanupObjectKey = oldObjectKeyToDelete;
        try {
          await objectStorage.deleteObject({ objectKey: cleanupObjectKey });
        } catch (error) {
          const failureMessage = readErrorMessage(error);
          cleanupWarning = failureMessage;
          await updateData((current) => ({
            ...current,
            objectCleanupFailures: [
              ...current.objectCleanupFailures,
              {
                objectCleanupFailureId: runtime.ports.newId('objectCleanupFailure'),
                objectKey: cleanupObjectKey,
                purpose: 'assignment_submission_overwrite',
                relatedEntityType: 'assignment_submission',
                relatedEntityId: submissionForResult.assignmentSubmissionId,
                failureMessage,
                status: 'pending',
                createdAt: runtime.ports.now(),
                resolvedAt: null
              }
            ]
          }));
        }
      }

      return {
        submission: {
          assignmentSubmissionId: submissionForResult.assignmentSubmissionId,
          submittedAt: submissionForResult.submittedAt,
          fileName: submissionForResult.fileName
        },
        cleanupWarning
      };
    },

    getAssignmentSubmissionAccess: async ({ assignmentSubmissionId }) => {
      const userId = await requireCurrentUserId();
      const data = await loadData();
      const currentMembership = await requireCurrentMembership(data, userId);
      const currentSeasonId = getCurrentSeasonId(data, currentMembership.choirId);
      const currentSeasonMembership = requireCurrentSeasonMembership(data, currentMembership, currentSeasonId);
      const submission = data.assignmentSubmissions.find(
        (candidate) => candidate.assignmentSubmissionId === assignmentSubmissionId
      );

      if (!submission) {
        throw new Error('Assignment submission not found');
      }

      if (submission.choirId !== currentMembership.choirId || submission.seasonId !== currentSeasonId) {
        throw new Error('Assignment submission belongs to a different choir or season');
      }

      const assignment = findAssignmentForService(data, submission.assignmentId, currentMembership, currentSeasonId);
      const resolvedDeliveryRules = resolveAssignmentDeliveryRuleViews(data, assignment);
      const isSubmitter = submission.submitterSeasonMembershipId === currentSeasonMembership.seasonMembershipId;
      const isReviewer = resolvedDeliveryRules.some(
        (rule) =>
          rule.submitterIds.includes(submission.submitterSeasonMembershipId) &&
          rule.reviewerIds.includes(currentSeasonMembership.seasonMembershipId)
      );

      if (!isSubmitter && !isReviewer) {
        throw new Error('Assignment submission access is not allowed');
      }

      return await objectStorage.createReadAuthorization({
        objectKey: submission.objectKey
      });
    },

    getResourcesRoot: async ({ workspaceContext }) => {
      const data = await loadData();
      const { currentMembership } = await requireWorkspaceContext(data, workspaceContext);

      return {
        scoresEntry: {
          label: '乐谱',
          songCount: data.songs.filter(
            (song) =>
              song.choirId === workspaceContext.currentChoirId && song.seasonId === workspaceContext.currentSeasonId
          ).length
        },
        folders: data.resourceFolders
          .filter(
            (folder) =>
              folder.choirId === workspaceContext.currentChoirId &&
              folder.seasonId === workspaceContext.currentSeasonId &&
              folder.placement === 'ordinary' &&
              folder.parentFolderId === null
          )
          .map(toResourceFolderListItem),
        files: data.resourceFiles
          .filter(
            (file) =>
              file.choirId === workspaceContext.currentChoirId &&
              file.seasonId === workspaceContext.currentSeasonId &&
              file.placement === 'ordinary' &&
              file.parentFolderId === null
          )
          .map(toResourceFileListItem),
        canManageOrdinaryResources: currentMembership.role === 'admin'
      };
    },

    createResourceFolder: async ({ workspaceContext, parentFolderId, name }) => {
      let folder: ResourceFolder | null = null;
      await updateData(async (current) => {
        const { actor, currentSeasonId } = await requireAdminWorkspaceContext(current, workspaceContext);
        const actorSeasonMembership = requireCurrentSeasonMembership(current, actor, currentSeasonId);
        requireResourceParentFolder(current, {
          choirId: actor.choirId,
          seasonId: currentSeasonId,
          placement: 'ordinary',
          songId: null,
          parentFolderId
        });
        const result = createResourceFolderInDomain(current, {
          choirId: actor.choirId,
          seasonId: currentSeasonId,
          actorSeasonMembershipId: actorSeasonMembership.seasonMembershipId,
          placement: 'ordinary',
          songId: null,
          parentFolderId,
          name,
          ports: resourceRulePorts
        });
        folder = result.folder;
        return result.data;
      });

      if (folder === null) {
        throw new Error('Resource folder was not created');
      }

      return toResourceFolderListItem(folder);
    },

    createSong: async ({ workspaceContext, name }) => {
      let song: Song | null = null;
      const data = await updateData(async (current) => {
        const { actor, currentSeasonId } = await requireAdminWorkspaceContext(current, workspaceContext);
        const actorSeasonMembership = requireCurrentSeasonMembership(current, actor, currentSeasonId);
        const result = createSongInDomain(current, {
          choirId: actor.choirId,
          seasonId: currentSeasonId,
          actorSeasonMembershipId: actorSeasonMembership.seasonMembershipId,
          name,
          ports: resourceRulePorts
        });
        song = result.song;
        return result.data;
      });

      if (song === null) {
        throw new Error('Song was not created');
      }

      return toSongListItem(data, song);
    },

    listSongs: async ({ workspaceContext }) => {
      const data = await loadData();
      await requireWorkspaceContext(data, workspaceContext);

      return data.songs
        .filter(
          (song) =>
            song.choirId === workspaceContext.currentChoirId && song.seasonId === workspaceContext.currentSeasonId
        )
        .map((song) => toSongListItem(data, song));
    },

    getSongDetail: async ({ workspaceContext, songId, folderId = null }) => {
      const data = await loadData();
      await requireWorkspaceContext(data, workspaceContext);
      const song = findSongForService(data, songId, workspaceContext.currentChoirId, workspaceContext.currentSeasonId);
      requireResourceParentFolder(data, {
        choirId: workspaceContext.currentChoirId,
        seasonId: workspaceContext.currentSeasonId,
        placement: 'song_related_resource',
        songId: song.songId,
        parentFolderId: folderId
      });

      return toSongDetailView(data, song, folderId);
    },

    createSongRelatedFolder: async ({ workspaceContext, songId, parentFolderId, name }) => {
      let folder: ResourceFolder | null = null;
      await updateData(async (current) => {
        const { actor, currentSeasonId } = await requireAdminWorkspaceContext(current, workspaceContext);
        const actorSeasonMembership = requireCurrentSeasonMembership(current, actor, currentSeasonId);
        const song = findSongForService(current, songId, actor.choirId, currentSeasonId);
        requireResourceParentFolder(current, {
          choirId: actor.choirId,
          seasonId: currentSeasonId,
          placement: 'song_related_resource',
          songId: song.songId,
          parentFolderId
        });
        const result = createResourceFolderInDomain(current, {
          choirId: actor.choirId,
          seasonId: currentSeasonId,
          actorSeasonMembershipId: actorSeasonMembership.seasonMembershipId,
          placement: 'song_related_resource',
          songId: song.songId,
          parentFolderId,
          name,
          ports: resourceRulePorts
        });
        folder = result.folder;
        return result.data;
      });

      if (folder === null) {
        throw new Error('Resource folder was not created');
      }

      return toResourceFolderListItem(folder);
    },

    createResourceUploadIntent: async ({ workspaceContext, fileName, mimeType, sizeBytes }) => {
      const data = await loadData();
      const { actor, currentSeasonId } = await requireAdminWorkspaceContext(data, workspaceContext);
      const actorSeasonMembership = requireCurrentSeasonMembership(data, actor, currentSeasonId);
      const normalizedFileName = normalizeRequiredText(fileName, 'Resource file name');
      const normalizedMimeType = mimeType.trim().toLowerCase();
      const normalizedExtension = normalizeResourceFileExtension(normalizedFileName);
      const fileUploadId = runtime.ports.newId('fileUpload');
      const objectKey = `resources/${currentSeasonId}/${fileUploadId}.${normalizedExtension}`;
      const upload = await objectStorage.createUploadAuthorization({
        objectKey,
        mimeType: normalizedMimeType,
        sizeBytes
      });
      const createdAt = runtime.ports.now();

      await updateData((current) => ({
        ...current,
        fileUploads: [
          ...current.fileUploads,
          {
            fileUploadId,
            purpose: 'resource_file',
            status: 'pending',
            choirId: actor.choirId,
            seasonId: currentSeasonId,
            assignmentId: null,
            createdByUserId: actor.userId,
            createdBySeasonMembershipId: actorSeasonMembership.seasonMembershipId,
            objectKey,
            fileName: normalizedFileName,
            mimeType: normalizedMimeType,
            extension: normalizedExtension,
            sizeBytes,
            etag: null,
            expiresAt: upload.expiresAt,
            createdAt,
            completedAt: null,
            attachedAt: null
          }
        ]
      }));

      return {
        fileUploadId,
        objectKey,
        upload
      };
    },

    uploadResourceFile: async ({ upload, file }) => {
      await objectStorage.uploadObject({ upload, file });
    },

    attachOrdinaryResourceFile: async ({ workspaceContext, parentFolderId, fileUploadId }) => {
      let file: ResourceFile | null = null;
      await updateData(async (current) => {
        const { actor, currentSeasonId } = await requireAdminWorkspaceContext(current, workspaceContext);
        const actorSeasonMembership = requireCurrentSeasonMembership(current, actor, currentSeasonId);
        const upload = requireCompletedResourceUploadForService(current, {
          fileUploadId,
          choirId: actor.choirId,
          seasonId: currentSeasonId,
          userId: actor.userId,
          seasonMembershipId: actorSeasonMembership.seasonMembershipId
        });
        requireResourceParentFolder(current, {
          choirId: actor.choirId,
          seasonId: currentSeasonId,
          placement: 'ordinary',
          songId: null,
          parentFolderId
        });
        const result = attachResourceFileInDomain(current, {
          choirId: actor.choirId,
          seasonId: currentSeasonId,
          actorSeasonMembershipId: actorSeasonMembership.seasonMembershipId,
          placement: 'ordinary',
          songId: null,
          parentFolderId,
          upload,
          ports: resourceRulePorts
        });
        file = result.file;
        return markResourceUploadAttached(result.data, fileUploadId, runtime.ports.now());
      });

      if (file === null) {
        throw new Error('Resource file was not attached');
      }

      return toResourceFileListItem(file);
    },

    attachSongRelatedResourceFile: async ({ workspaceContext, songId, parentFolderId, fileUploadId }) => {
      let file: ResourceFile | null = null;
      await updateData(async (current) => {
        const { actor, currentSeasonId } = await requireAdminWorkspaceContext(current, workspaceContext);
        const actorSeasonMembership = requireCurrentSeasonMembership(current, actor, currentSeasonId);
        const song = findSongForService(current, songId, actor.choirId, currentSeasonId);
        const upload = requireCompletedResourceUploadForService(current, {
          fileUploadId,
          choirId: actor.choirId,
          seasonId: currentSeasonId,
          userId: actor.userId,
          seasonMembershipId: actorSeasonMembership.seasonMembershipId
        });
        requireResourceParentFolder(current, {
          choirId: actor.choirId,
          seasonId: currentSeasonId,
          placement: 'song_related_resource',
          songId: song.songId,
          parentFolderId
        });
        const result = attachResourceFileInDomain(current, {
          choirId: actor.choirId,
          seasonId: currentSeasonId,
          actorSeasonMembershipId: actorSeasonMembership.seasonMembershipId,
          placement: 'song_related_resource',
          songId: song.songId,
          parentFolderId,
          upload,
          ports: resourceRulePorts
        });
        file = result.file;
        return markResourceUploadAttached(result.data, fileUploadId, runtime.ports.now());
      });

      if (file === null) {
        throw new Error('Resource file was not attached');
      }

      return toResourceFileListItem(file);
    },

    updateSongCurrentScore: async ({ workspaceContext, songId, fileUploadId, updateNote }) => {
      let oldObjectKeyToDelete: string | null = null;
      const data = await updateData(async (current) => {
        const { actor, currentSeasonId } = await requireAdminWorkspaceContext(current, workspaceContext);
        const actorSeasonMembership = requireCurrentSeasonMembership(current, actor, currentSeasonId);
        const song = findSongForService(current, songId, actor.choirId, currentSeasonId);
        const upload = requireCompletedResourceUploadForService(current, {
          fileUploadId,
          choirId: actor.choirId,
          seasonId: currentSeasonId,
          userId: actor.userId,
          seasonMembershipId: actorSeasonMembership.seasonMembershipId
        });
        const replaced = replaceSongCurrentScore(current, {
          songId: song.songId,
          actorSeasonMembershipId: actorSeasonMembership.seasonMembershipId,
          upload,
          updateNote,
          ports: resourceRulePorts
        });
        const notified = createSongScoreUpdatedNotification(replaced.data, {
          choirId: actor.choirId,
          seasonId: currentSeasonId,
          actorSeasonMembershipId: actorSeasonMembership.seasonMembershipId,
          songId: replaced.song.songId,
          songName: replaced.song.name,
          updateNote: replaced.currentScore.updateNote,
          ports: resourceRulePorts
        });
        oldObjectKeyToDelete = replaced.oldObjectKey;
        return markResourceUploadAttached(notified.data, fileUploadId, runtime.ports.now());
      });

      if (
        oldObjectKeyToDelete !== null &&
        !data.resourceFiles.some((file) => file.objectKey === oldObjectKeyToDelete)
      ) {
        const cleanupObjectKey = oldObjectKeyToDelete;
        try {
          await objectStorage.deleteObject({ objectKey: cleanupObjectKey });
        } catch (error) {
          const failureMessage = readErrorMessage(error);
          await updateData((current) => ({
            ...current,
            objectCleanupFailures: [
              ...current.objectCleanupFailures,
              {
                objectCleanupFailureId: runtime.ports.newId('objectCleanupFailure'),
                objectKey: cleanupObjectKey,
                purpose: 'song_current_score_replace',
                relatedEntityType: 'song',
                relatedEntityId: songId,
                failureMessage,
                status: 'pending',
                createdAt: runtime.ports.now(),
                resolvedAt: null
              }
            ]
          }));
        }
      }

      const song = findSongForService(data, songId, workspaceContext.currentChoirId, workspaceContext.currentSeasonId);
      return toSongDetailView(data, song, null);
    },

    getResourceFileAccess: async ({ workspaceContext, resourceFileId }) => {
      const data = await loadData();
      await requireWorkspaceContext(data, workspaceContext);
      const file = findResourceFileForService(
        data,
        resourceFileId,
        workspaceContext.currentChoirId,
        workspaceContext.currentSeasonId
      );
      const access = await objectStorage.createReadAuthorization({ objectKey: file.objectKey });

      return {
        ...access,
        mimeType: file.mimeType,
        extension: file.extension,
        fileName: file.fileName
      };
    },

    readMarkdownResourceFile: async ({ workspaceContext, resourceFileId }) => {
      const data = await loadData();
      await requireWorkspaceContext(data, workspaceContext);
      const file = findResourceFileForService(
        data,
        resourceFileId,
        workspaceContext.currentChoirId,
        workspaceContext.currentSeasonId
      );
      if (!isMarkdownResourceFile(file)) {
        throw new Error(MARKDOWN_PREVIEW_UNSUPPORTED_MESSAGE);
      }

      return {
        fileName: file.fileName,
        markdown: await objectStorage.readTextObject({ objectKey: file.objectKey })
      };
    },

    listNotifications: async ({ workspaceContext }) => {
      const data = await loadData();
      const { currentSeasonMembership } = await requireWorkspaceContext(data, workspaceContext);

      return listNotificationsForMember(data, {
        choirId: workspaceContext.currentChoirId,
        seasonId: workspaceContext.currentSeasonId,
        seasonMembershipId: currentSeasonMembership.seasonMembershipId
      });
    },

    getUnreadNotificationState: async ({ workspaceContext }) => {
      const data = await loadData();
      const { currentSeasonMembership } = await requireWorkspaceContext(data, workspaceContext);
      const notifications = listNotificationsForMember(data, {
        choirId: workspaceContext.currentChoirId,
        seasonId: workspaceContext.currentSeasonId,
        seasonMembershipId: currentSeasonMembership.seasonMembershipId
      });

      return {
        hasUnread: notifications.some((notification) => notification.readAt === null)
      };
    },

    markNotificationRead: async ({ workspaceContext, notificationId }) => {
      let wasVisible = false;
      const data = await updateData(async (current) => {
        const { currentSeasonMembership } = await requireWorkspaceContext(current, workspaceContext);
        const visibleNotification = listNotificationsForMember(current, {
          choirId: workspaceContext.currentChoirId,
          seasonId: workspaceContext.currentSeasonId,
          seasonMembershipId: currentSeasonMembership.seasonMembershipId
        }).find((notification) => notification.notificationId === notificationId);

        if (!visibleNotification) {
          return current;
        }

        wasVisible = true;
        return markNotificationReadInDomain(current, {
          notificationId,
          seasonMembershipId: currentSeasonMembership.seasonMembershipId,
          now: runtime.ports.now()
        }).data;
      });

      if (!wasVisible) {
        return null;
      }

      const { currentSeasonMembership } = await requireWorkspaceContext(data, workspaceContext);

      return (
        listNotificationsForMember(data, {
          choirId: workspaceContext.currentChoirId,
          seasonId: workspaceContext.currentSeasonId,
          seasonMembershipId: currentSeasonMembership.seasonMembershipId
        }).find((notification) => notification.notificationId === notificationId) ?? null
      );
    },

    getViewModel: async () => {
      let data = await loadData();
      const currentUserId = await runtime.session.getCurrentUserId();
      const currentUser = data.users.find((user) => user.userId === currentUserId) ?? null;
      const joinedChoirs = currentUser ? getJoinedChoirs(data, currentUser.userId) : [];
      const currentChoirId = await runtime.session.getCurrentChoirId();
      const currentChoir = joinedChoirs.find((choir) => choir.choirId === currentChoirId) ?? null;
      const currentMembership =
        currentUser && currentChoir ? findMembership(data, currentUser.userId, currentChoir.choirId) : null;
      const currentSeasonId = currentChoir ? getCurrentSeasonId(data, currentChoir.choirId) : null;
      const now = runtime.ports.now();

      const viewScheduleIds =
        currentMembership && currentChoir && currentSeasonId
          ? data.schedules
              .filter(
                (schedule) =>
                  schedule.choirId === currentChoir.choirId &&
                  schedule.seasonId === currentSeasonId &&
                  schedule.status === 'active'
              )
              .map((schedule) => schedule.scheduleId)
          : [];

      if (viewScheduleIds.length > 0) {
        data = await settleScheduleReadDataIfNeeded(data, viewScheduleIds, now);
      }

      const memberGroups =
        currentChoir && currentSeasonId
          ? data.memberGroups
              .filter((group) => group.seasonId === currentSeasonId)
              .sort((left, right) => left.displayOrder - right.displayOrder)
          : [];
      const members =
        currentChoir && currentSeasonId ? getCurrentSeasonMembers(data, currentChoir.choirId, currentSeasonId) : [];
      const currentSeason =
        currentChoir && currentSeasonId
          ? (data.seasons.find((season) => season.seasonId === currentSeasonId) ?? null)
          : null;
      const isCurrentUserAdmin = currentMembership?.role === 'admin';
      const currentPermissionKeys =
        currentMembership && currentSeasonId
          ? resolveCurrentPermissionKeys(data, currentMembership, currentSeasonId)
          : [];
      const availableInvitationCodes =
        currentChoir && currentSeasonId
          ? data.invitationCodes.filter(
              (code) =>
                code.choirId === currentChoir.choirId &&
                code.seasonId === currentSeasonId &&
                code.status === 'active' &&
                code.expiresAt > now
            )
          : [];
      const currentChoirCurrentSeasonPendingJoinRequests =
        currentChoir && currentSeasonId
          ? data.joinRequests.filter(
              (request) =>
                request.choirId === currentChoir.choirId &&
                request.seasonId === currentSeasonId &&
                request.status === 'pending'
            )
          : [];
      const canApproveJoinRequests = currentPermissionKeys.includes('member_approval');
      const pendingJoinRequests = canApproveJoinRequests
        ? currentChoirCurrentSeasonPendingJoinRequests.map((request) => toPendingJoinRequestListItem(data, request))
        : [];
      const ownJoinRequests = currentUser
        ? data.joinRequests.filter((request) => request.userId === currentUser.userId)
        : [];
      const memberGroupSummaries =
        currentMembership && currentChoir && currentSeasonId
          ? memberGroups.map((group) => toMemberGroupSummary(data, group, currentMembership))
          : [];
      const schedules =
        currentMembership && currentChoir && currentSeasonId
          ? data.schedules
              .filter(
                (schedule) =>
                  schedule.choirId === currentChoir.choirId &&
                  schedule.seasonId === currentSeasonId &&
                  schedule.status === 'active'
              )
              .sort(compareSchedulesByStart)
              .map((schedule) => toScheduleListItem(data, schedule, currentMembership, now))
          : [];
      const attendanceCategories =
        currentChoir && currentSeasonId
          ? findAttendanceCategoriesForSeason(data, currentChoir.choirId, currentSeasonId)
          : [];

      return {
        currentUser,
        currentChoir,
        currentMembership,
        currentSeason,
        isCurrentUserAdmin,
        currentPermissionKeys,
        joinedChoirs,
        memberGroups,
        memberGroupSummaries,
        members,
        availableInvitationCodes,
        pendingJoinRequests,
        ownJoinRequests,
        schedules,
        attendanceCategories
      };
    }
  };

  async function rollbackRegistration(userId: string): Promise<unknown> {
    let rollbackError: unknown = null;

    try {
      await updateData((current) =>
        current.users.some((candidate) => candidate.userId === userId)
          ? {
              ...current,
              users: current.users.filter((candidate) => candidate.userId !== userId)
            }
          : current
      );
    } catch (error) {
      rollbackError = error;
    }

    try {
      await runtime.auth.deleteCredential({ userId });
    } catch (error) {
      rollbackError ??= error;
    }

    try {
      await runtime.session.clear();
    } catch (error) {
      rollbackError ??= error;
    }

    return rollbackError;
  }
}

function toResourceFolderListItem(folder: ResourceFolder): ResourceFolderListItem {
  return {
    resourceFolderId: folder.resourceFolderId,
    name: folder.name,
    createdAt: folder.createdAt
  };
}

function toResourceFileListItem(file: ResourceFile): ResourceFileListItem {
  return {
    resourceFileId: file.resourceFileId,
    fileName: file.fileName,
    mimeType: file.mimeType,
    extension: file.extension,
    sizeBytes: file.sizeBytes,
    createdAt: file.createdAt,
    canPreview: isMarkdownResourceFile(file)
  };
}

function toSongListItem(data: MemberManagementData, song: Song): SongListItem {
  const currentScore = song.currentScoreResourceFileId
    ? data.resourceFiles.find((file) => file.resourceFileId === song.currentScoreResourceFileId)
    : null;

  if (song.currentScoreResourceFileId !== null && !currentScore) {
    throw new Error('Current score file not found');
  }

  return {
    songId: song.songId,
    name: song.name,
    currentScoreFileName: currentScore?.fileName ?? null,
    currentScoreUpdatedAt: currentScore?.createdAt ?? null
  };
}

function toSongDetailView(data: MemberManagementData, song: Song, folderId: string | null): SongDetailView {
  const currentScore = song.currentScoreResourceFileId
    ? data.resourceFiles.find((file) => file.resourceFileId === song.currentScoreResourceFileId)
    : null;

  if (song.currentScoreResourceFileId !== null && !currentScore) {
    throw new Error('Current score file not found');
  }

  return {
    song: toSongListItem(data, song),
    currentScore: currentScore ? toResourceFileListItem(currentScore) : null,
    relatedResources: {
      folderId,
      folders: data.resourceFolders
        .filter(
          (folder) =>
            folder.choirId === song.choirId &&
            folder.seasonId === song.seasonId &&
            folder.placement === 'song_related_resource' &&
            folder.songId === song.songId &&
            folder.parentFolderId === folderId
        )
        .map(toResourceFolderListItem),
      files: data.resourceFiles
        .filter(
          (file) =>
            file.choirId === song.choirId &&
            file.seasonId === song.seasonId &&
            file.placement === 'song_related_resource' &&
            file.songId === song.songId &&
            file.parentFolderId === folderId
        )
        .map(toResourceFileListItem)
    }
  };
}

function findSongForService(data: MemberManagementData, songId: string, choirId: string, seasonId: string): Song {
  const song = data.songs.find((candidate) => candidate.songId === songId);

  if (!song) {
    throw new Error('Song not found');
  }

  if (song.choirId !== choirId || song.seasonId !== seasonId) {
    throw new Error('Song belongs to a different choir or season');
  }

  return song;
}

function findResourceFileForService(
  data: MemberManagementData,
  resourceFileId: string,
  choirId: string,
  seasonId: string
): ResourceFile {
  const file = data.resourceFiles.find((candidate) => candidate.resourceFileId === resourceFileId);

  if (!file) {
    throw new Error('Resource file not found');
  }

  if (file.choirId !== choirId || file.seasonId !== seasonId) {
    throw new Error('Resource file belongs to a different choir or season');
  }

  return file;
}

function requireCompletedResourceUploadForService(
  data: MemberManagementData,
  input: {
    readonly fileUploadId: string;
    readonly choirId: string;
    readonly seasonId: string;
    readonly userId: string;
    readonly seasonMembershipId: string;
  }
): FileUpload & { readonly purpose: 'resource_file'; readonly status: 'completed'; readonly assignmentId: null } {
  const upload = data.fileUploads.find((candidate) => candidate.fileUploadId === input.fileUploadId);

  if (!upload) {
    throw new Error('File upload not found');
  }

  if (
    upload.status !== 'completed' ||
    upload.purpose !== 'resource_file' ||
    upload.assignmentId !== null ||
    upload.choirId !== input.choirId ||
    upload.seasonId !== input.seasonId ||
    upload.createdByUserId !== input.userId ||
    upload.createdBySeasonMembershipId !== input.seasonMembershipId
  ) {
    throw new Error('Completed resource upload does not match current member');
  }

  if (data.resourceFiles.some((file) => file.fileUploadId === upload.fileUploadId)) {
    throw new Error('Resource upload has already been attached');
  }

  if (upload.attachedAt !== null) {
    throw new Error('Resource upload has already been attached');
  }

  return {
    ...upload,
    purpose: 'resource_file',
    status: 'completed',
    assignmentId: null
  };
}

function markResourceUploadAttached(
  data: MemberManagementData,
  fileUploadId: string,
  attachedAt: string
): MemberManagementData {
  return {
    ...data,
    fileUploads: data.fileUploads.map((upload) =>
      upload.fileUploadId === fileUploadId
        ? {
            ...upload,
            attachedAt
          }
        : upload
    )
  };
}

function requireResourceParentFolder(
  data: MemberManagementData,
  input: {
    readonly choirId: string;
    readonly seasonId: string;
    readonly placement: ResourceFolderPlacement;
    readonly songId: string | null;
    readonly parentFolderId: string | null;
  }
): ResourceFolder | null {
  if (input.parentFolderId === null) {
    return null;
  }

  const parent = data.resourceFolders.find((folder) => folder.resourceFolderId === input.parentFolderId);
  if (!parent) {
    throw new Error('Resource parent folder not found');
  }

  if (
    parent.choirId !== input.choirId ||
    parent.seasonId !== input.seasonId ||
    parent.placement !== input.placement ||
    parent.songId !== input.songId
  ) {
    throw new Error('Resource parent folder does not match resource placement');
  }

  return parent;
}

function normalizeResourceFileExtension(fileName: string): string {
  const extension = getLocalFileExtension(fileName);

  if (extension === '') {
    throw new Error('Resource file extension is required');
  }

  return extension;
}

function isMarkdownResourceFile(file: Pick<ResourceFile, 'extension' | 'mimeType'>): boolean {
  return file.mimeType === 'text/markdown' || file.extension === 'md' || file.extension === 'markdown';
}

function mergeScheduleUpdateDraft(existing: Schedule, draft: ScheduleUpdateDraftInput): MergedScheduleUpdateDraftInput {
  return {
    title: draft.title ?? existing.title,
    startsAt: draft.startsAt ?? existing.startsAt,
    endsAt: draft.endsAt ?? existing.endsAt,
    location: draft.location ?? existing.location,
    note: draft.note ?? existing.note,
    ...(draft.attendance !== undefined ? { attendance: draft.attendance } : {})
  };
}

function assertScheduleAttendanceIntentExplicit(draft: ScheduleDraftInput): void {
  if (!Object.prototype.hasOwnProperty.call(draft, 'attendance') || draft.attendance === undefined) {
    throw new Error('Schedule attendance must be explicit');
  }
}

function normalizeScheduleListLimit(limit: number): number {
  if (!Number.isInteger(limit) || limit < 1 || limit > 20) {
    throw new Error('Schedule list limit must be between 1 and 20');
  }
  return limit;
}

function toScheduleParticipantMemberGroupOption(memberGroup: MemberGroup): ScheduleParticipantMemberGroupOption {
  return {
    memberGroupId: memberGroup.memberGroupId,
    name: memberGroup.name,
    isDefault: memberGroup.isDefault,
    displayOrder: memberGroup.displayOrder
  };
}

function requireCurrentSeasonMembership(
  data: MemberManagementData,
  membership: Membership,
  seasonId: string
): SeasonMembership {
  const seasonMembership = data.seasonMemberships.find(
    (candidate) =>
      candidate.membershipId === membership.membershipId &&
      candidate.choirId === membership.choirId &&
      candidate.seasonId === seasonId &&
      candidate.status === 'active'
  );

  if (!seasonMembership) {
    throw new Error('Current season membership is required');
  }

  return seasonMembership;
}

function canManageAssignment(
  data: MemberManagementData,
  assignment: Assignment,
  membership: Membership,
  currentSeasonId: string
): boolean {
  if (membership.role === 'admin') {
    return true;
  }

  if (assignment.createdByMembershipId !== membership.membershipId) {
    return false;
  }

  return resolveCurrentPermissionKeys(data, membership, currentSeasonId).includes('assignment_publish_manage');
}

function findAssignmentForService(
  data: MemberManagementData,
  assignmentId: string,
  currentMembership: Membership,
  currentSeasonId: string
): Assignment {
  const assignment = data.assignments.find((candidate) => candidate.assignmentId === assignmentId);

  if (!assignment) {
    throw new Error('Assignment not found');
  }

  if (assignment.choirId !== currentMembership.choirId || assignment.seasonId !== currentSeasonId) {
    throw new Error('Assignment belongs to a different choir or season');
  }

  if (assignment.status !== 'published') {
    throw new Error('Assignment is not published');
  }

  return assignment;
}

function toAssignmentDetailForService(
  data: MemberManagementData,
  assignmentId: string,
  currentMembership: Membership,
  nowIso: string
): AssignmentDetail {
  const currentSeasonId = getCurrentSeasonId(data, currentMembership.choirId);
  const currentSeasonMembership = requireCurrentSeasonMembership(data, currentMembership, currentSeasonId);
  const assignment = findAssignmentForService(data, assignmentId, currentMembership, currentSeasonId);

  if (!canViewAssignment(data, assignment, currentMembership, currentSeasonMembership)) {
    throw new Error('Assignment is not available to current member');
  }

  return projectAssignmentDetail({
    assignment,
    currentSeasonMembershipId: currentSeasonMembership.seasonMembershipId,
    canManage: canManageAssignment(data, assignment, currentMembership, currentSeasonId),
    nowIso,
    members: getAssignmentViewMembers(data, assignment.choirId, assignment.seasonId),
    resolvedDeliveryRules: resolveAssignmentDeliveryRuleViews(data, assignment),
    deliveryRules: getAssignmentDeliveryRuleViewSources(data, assignment),
    submissions: data.assignmentSubmissions
  });
}

function canViewAssignment(
  data: MemberManagementData,
  assignment: Assignment,
  currentMembership: Membership,
  currentSeasonMembership: SeasonMembership
): boolean {
  if (canManageAssignment(data, assignment, currentMembership, assignment.seasonId)) {
    return true;
  }

  const currentSeasonMembershipId = currentSeasonMembership.seasonMembershipId;
  return resolveAssignmentDeliveryRuleViews(data, assignment).some(
    (rule) =>
      rule.submitterIds.includes(currentSeasonMembershipId) || rule.reviewerIds.includes(currentSeasonMembershipId)
  );
}

function assertCurrentMemberCanSubmitAssignment(
  data: MemberManagementData,
  assignment: Assignment,
  currentSeasonMembership: SeasonMembership
): void {
  const isExpectedSubmitter = resolveAssignmentDeliveryRuleViews(data, assignment).some((rule) =>
    rule.submitterIds.includes(currentSeasonMembership.seasonMembershipId)
  );

  if (!isExpectedSubmitter) {
    throw new Error('Assignment is not assigned to current member');
  }
}

function buildAssignmentDeliveryRows(
  assignmentId: string,
  deliveryRules: readonly AssignmentDeliveryRuleDraftInput[],
  ports: DomainPorts
): {
  readonly rules: readonly AssignmentDeliveryRule[];
  readonly targets: readonly AssignmentDeliveryRuleTarget[];
  readonly reviewers: readonly AssignmentDeliveryRuleReviewer[];
} {
  const rules: AssignmentDeliveryRule[] = [];
  const targets: AssignmentDeliveryRuleTarget[] = [];
  const reviewers: AssignmentDeliveryRuleReviewer[] = [];

  deliveryRules.forEach((deliveryRule, displayOrder) => {
    const assignmentDeliveryRuleId = ports.newId('assignmentDeliveryRule');
    rules.push({
      assignmentDeliveryRuleId,
      assignmentId,
      displayOrder
    });

    for (const memberGroupId of deliveryRule.target.memberGroupIds) {
      targets.push({
        assignmentDeliveryRuleTargetId: ports.newId('assignmentDeliveryRule'),
        assignmentDeliveryRuleId,
        selectorType: 'member_group',
        memberGroupId,
        seasonMembershipId: null
      });
    }

    for (const seasonMembershipId of deliveryRule.target.includeSeasonMembershipIds) {
      targets.push({
        assignmentDeliveryRuleTargetId: ports.newId('assignmentDeliveryRule'),
        assignmentDeliveryRuleId,
        selectorType: 'season_membership_include',
        memberGroupId: null,
        seasonMembershipId
      });
    }

    for (const seasonMembershipId of deliveryRule.target.excludeSeasonMembershipIds) {
      targets.push({
        assignmentDeliveryRuleTargetId: ports.newId('assignmentDeliveryRule'),
        assignmentDeliveryRuleId,
        selectorType: 'season_membership_exclude',
        memberGroupId: null,
        seasonMembershipId
      });
    }

    for (const memberGroupId of deliveryRule.reviewers.memberGroupIds) {
      reviewers.push({
        assignmentDeliveryRuleReviewerId: ports.newId('assignmentDeliveryRule'),
        assignmentDeliveryRuleId,
        selectorType: 'member_group',
        memberGroupId,
        memberGroupTagId: null,
        seasonMembershipId: null
      });
    }

    for (const memberGroupTagId of deliveryRule.reviewers.memberGroupTagIds) {
      reviewers.push({
        assignmentDeliveryRuleReviewerId: ports.newId('assignmentDeliveryRule'),
        assignmentDeliveryRuleId,
        selectorType: 'member_group_tag',
        memberGroupId: null,
        memberGroupTagId,
        seasonMembershipId: null
      });
    }

    for (const seasonMembershipId of deliveryRule.reviewers.seasonMembershipIds) {
      reviewers.push({
        assignmentDeliveryRuleReviewerId: ports.newId('assignmentDeliveryRule'),
        assignmentDeliveryRuleId,
        selectorType: 'season_membership',
        memberGroupId: null,
        memberGroupTagId: null,
        seasonMembershipId
      });
    }
  });

  return { rules, targets, reviewers };
}

function assertResolvableDeliveryRows(
  data: MemberManagementData,
  assignment: Assignment,
  rows: {
    readonly rules: readonly AssignmentDeliveryRule[];
    readonly targets: readonly AssignmentDeliveryRuleTarget[];
    readonly reviewers: readonly AssignmentDeliveryRuleReviewer[];
  }
): void {
  resolveDeliveryRules({
    deliveryRules: rows.rules
      .slice()
      .sort((left, right) => left.displayOrder - right.displayOrder)
      .map((rule) => ({
        target: rows.targets.filter((target) => target.assignmentDeliveryRuleId === rule.assignmentDeliveryRuleId),
        reviewers: rows.reviewers.filter(
          (reviewer) => reviewer.assignmentDeliveryRuleId === rule.assignmentDeliveryRuleId
        )
      })),
    ...getAssignmentRuleResolutionContext(data, assignment.choirId, assignment.seasonId)
  });
}

function resolveAssignmentDeliveryRuleViews(
  data: MemberManagementData,
  assignment: Assignment
): readonly ResolvedAssignmentDeliveryRuleView[] {
  const rules = data.assignmentDeliveryRules
    .filter((rule) => rule.assignmentId === assignment.assignmentId)
    .slice()
    .sort((left, right) => left.displayOrder - right.displayOrder);

  return resolveDeliveryRules({
    deliveryRules: rules.map((rule) => ({
      target: data.assignmentDeliveryRuleTargets.filter(
        (target) => target.assignmentDeliveryRuleId === rule.assignmentDeliveryRuleId
      ),
      reviewers: data.assignmentDeliveryRuleReviewers.filter(
        (reviewer) => reviewer.assignmentDeliveryRuleId === rule.assignmentDeliveryRuleId
      )
    })),
    ...getAssignmentRuleResolutionContext(data, assignment.choirId, assignment.seasonId)
  });
}

function getAssignmentRuleResolutionContext(
  data: MemberManagementData,
  choirId: string,
  seasonId: string
): {
  readonly members: readonly {
    readonly seasonMembershipId: string;
    readonly nickname: string;
    readonly memberGroupIds: readonly string[];
    readonly memberGroupTagIds: readonly string[];
  }[];
  readonly knownMemberGroupIds: readonly string[];
  readonly knownMemberGroupTagIds: readonly string[];
} {
  const currentSeasonMemberGroups = data.memberGroups.filter(
    (memberGroup) => memberGroup.choirId === choirId && memberGroup.seasonId === seasonId
  );
  const currentSeasonMemberGroupIds = new Set(
    currentSeasonMemberGroups.map((memberGroup) => memberGroup.memberGroupId)
  );
  const currentSeasonMemberGroupTags = data.memberGroupTags.filter((tag) =>
    currentSeasonMemberGroupIds.has(tag.memberGroupId)
  );
  const currentSeasonMemberGroupTagIds = new Set(currentSeasonMemberGroupTags.map((tag) => tag.memberGroupTagId));

  return {
    members: data.seasonMemberships
      .filter(
        (seasonMembership) =>
          seasonMembership.choirId === choirId &&
          seasonMembership.seasonId === seasonId &&
          seasonMembership.status === 'active'
      )
      .map((seasonMembership) => ({
        seasonMembershipId: seasonMembership.seasonMembershipId,
        nickname: seasonMembership.nickname,
        memberGroupIds: data.seasonMembershipMemberGroups
          .filter(
            (link) =>
              link.seasonMembershipId === seasonMembership.seasonMembershipId &&
              currentSeasonMemberGroupIds.has(link.memberGroupId)
          )
          .map((link) => link.memberGroupId),
        memberGroupTagIds: data.seasonMembershipMemberGroupTags
          .filter(
            (link) =>
              link.seasonMembershipId === seasonMembership.seasonMembershipId &&
              currentSeasonMemberGroupTagIds.has(link.memberGroupTagId)
          )
          .map((link) => link.memberGroupTagId)
      })),
    knownMemberGroupIds: currentSeasonMemberGroups.map((memberGroup) => memberGroup.memberGroupId),
    knownMemberGroupTagIds: currentSeasonMemberGroupTags.map((tag) => tag.memberGroupTagId)
  };
}

function getAssignmentViewMembers(
  data: MemberManagementData,
  choirId: string,
  seasonId: string
): readonly AssignmentViewMember[] {
  return getCurrentSeasonMembers(data, choirId, seasonId).map((member) => ({
    seasonMembershipId: member.seasonMembershipId,
    nickname: member.nickname,
    memberGroupNames: member.memberGroupNames
  }));
}

function getAssignmentDeliveryRuleViewSources(
  data: MemberManagementData,
  assignment: Assignment
): readonly AssignmentDeliveryRuleViewSource[] {
  return data.assignmentDeliveryRules
    .filter((rule) => rule.assignmentId === assignment.assignmentId)
    .slice()
    .sort((left, right) => left.displayOrder - right.displayOrder)
    .map((rule) => {
      const targets = data.assignmentDeliveryRuleTargets.filter(
        (target) => target.assignmentDeliveryRuleId === rule.assignmentDeliveryRuleId
      );
      const reviewers = data.assignmentDeliveryRuleReviewers.filter(
        (reviewer) => reviewer.assignmentDeliveryRuleId === rule.assignmentDeliveryRuleId
      );
      const target = {
        memberGroupIds: targets.flatMap((candidate) =>
          candidate.selectorType === 'member_group' && candidate.memberGroupId !== null ? [candidate.memberGroupId] : []
        ),
        includeSeasonMembershipIds: targets.flatMap((candidate) =>
          candidate.selectorType === 'season_membership_include' && candidate.seasonMembershipId !== null
            ? [candidate.seasonMembershipId]
            : []
        ),
        excludeSeasonMembershipIds: targets.flatMap((candidate) =>
          candidate.selectorType === 'season_membership_exclude' && candidate.seasonMembershipId !== null
            ? [candidate.seasonMembershipId]
            : []
        )
      };
      const reviewerDraft = {
        memberGroupIds: reviewers.flatMap((candidate) =>
          candidate.selectorType === 'member_group' && candidate.memberGroupId !== null ? [candidate.memberGroupId] : []
        ),
        memberGroupTagIds: reviewers.flatMap((candidate) =>
          candidate.selectorType === 'member_group_tag' && candidate.memberGroupTagId !== null
            ? [candidate.memberGroupTagId]
            : []
        ),
        seasonMembershipIds: reviewers.flatMap((candidate) =>
          candidate.selectorType === 'season_membership' && candidate.seasonMembershipId !== null
            ? [candidate.seasonMembershipId]
            : []
        )
      };

      return {
        assignmentDeliveryRuleId: rule.assignmentDeliveryRuleId,
        targetSummary: summarizeAssignmentTargets(data, target),
        reviewerSummary: summarizeAssignmentReviewers(data, reviewerDraft),
        target,
        reviewers: reviewerDraft
      };
    });
}

function summarizeAssignmentTargets(
  data: MemberManagementData,
  target: AssignmentDeliveryRuleDraftInput['target']
): string {
  return [
    ...target.memberGroupIds.map((memberGroupId) => findMemberGroupName(data, memberGroupId)),
    ...target.includeSeasonMembershipIds.map(
      (seasonMembershipId) => `包含 ${findSeasonMembershipNickname(data, seasonMembershipId)}`
    ),
    ...target.excludeSeasonMembershipIds.map(
      (seasonMembershipId) => `排除 ${findSeasonMembershipNickname(data, seasonMembershipId)}`
    )
  ].join('、');
}

function summarizeAssignmentReviewers(
  data: MemberManagementData,
  reviewers: AssignmentDeliveryRuleDraftInput['reviewers']
): string {
  return [
    ...reviewers.memberGroupIds.map((memberGroupId) => findMemberGroupName(data, memberGroupId)),
    ...reviewers.memberGroupTagIds.map((memberGroupTagId) => findMemberGroupTagName(data, memberGroupTagId)),
    ...reviewers.seasonMembershipIds.map((seasonMembershipId) => findSeasonMembershipNickname(data, seasonMembershipId))
  ].join('、');
}

function findMemberGroupName(data: MemberManagementData, memberGroupId: string): string {
  const memberGroup = data.memberGroups.find((candidate) => candidate.memberGroupId === memberGroupId);

  if (!memberGroup) {
    throw new Error('Member group not found');
  }

  return memberGroup.name;
}

function findMemberGroupTagName(data: MemberManagementData, memberGroupTagId: string): string {
  const tag = data.memberGroupTags.find((candidate) => candidate.memberGroupTagId === memberGroupTagId);

  if (!tag) {
    throw new Error('Member group tag not found');
  }

  return tag.name;
}

function requireMemberGroupTagBelongsToGroup(
  data: MemberManagementData,
  memberGroupId: string,
  memberGroupTagId: string
): void {
  const tag = data.memberGroupTags.find((candidate) => candidate.memberGroupTagId === memberGroupTagId);

  if (!tag) {
    throw new Error('Member group tag not found');
  }

  if (tag.memberGroupId !== memberGroupId) {
    throw new Error('Member group tag does not belong to the provided member group');
  }
}

function findSeasonMembershipNickname(data: MemberManagementData, seasonMembershipId: string): string {
  const seasonMembership = data.seasonMemberships.find(
    (candidate) => candidate.seasonMembershipId === seasonMembershipId
  );

  if (!seasonMembership) {
    throw new Error('Season membership not found');
  }

  return seasonMembership.nickname;
}

function getLocalFileExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot === -1 ? '' : fileName.slice(lastDot + 1).toLowerCase();
}

function normalizeUploadExtension(extension: string, fileName: string): string {
  const normalizedExtension = extension.trim();
  if (
    normalizedExtension === '' ||
    normalizedExtension.startsWith('.') ||
    normalizedExtension !== normalizedExtension.toLowerCase()
  ) {
    throw new Error('Upload extension must be lower-case without a leading dot');
  }
  if (normalizedExtension !== getLocalFileExtension(fileName)) {
    throw new Error('Upload extension must match file name');
  }
  return normalizedExtension;
}

function readErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function assertStartedSchedulePatchDoesNotCarryLockedFields(
  existing: Schedule,
  draft: ScheduleUpdateDraftInput,
  now: string
): void {
  if (parseIsoMillis(now, 'now') < parseIsoMillis(existing.startsAt, 'schedule.startsAt')) {
    return;
  }

  if (
    hasOwnDraftField(draft, 'startsAt') ||
    hasOwnDraftField(draft, 'endsAt') ||
    hasOwnDraftField(draft, 'attendance')
  ) {
    throw new Error('Schedule has already started; attendance and time fields are locked');
  }
}

function hasOwnDraftField(draft: ScheduleUpdateDraftInput, field: keyof ScheduleUpdateDraftInput): boolean {
  return Object.prototype.hasOwnProperty.call(draft, field);
}

function resolveCurrentPermissionKeys(
  data: MemberManagementData,
  membership: Membership,
  seasonId: string
): readonly PermissionKey[] {
  const seasonMembership = data.seasonMemberships.find(
    (candidate) =>
      candidate.membershipId === membership.membershipId &&
      candidate.choirId === membership.choirId &&
      candidate.seasonId === seasonId &&
      candidate.status === 'active'
  );

  return resolveEffectivePermissionKeys({
    memberRole: membership.role,
    seasonMembershipId: seasonMembership?.seasonMembershipId,
    choirId: membership.choirId,
    seasonId,
    permissionGroups: data.permissionGroups,
    permissionGroupPermissions: data.permissionGroupPermissions,
    permissionGroupMembers: data.permissionGroupMembers
  });
}

function getJoinedChoirs(data: MemberManagementData, userId: string): readonly Choir[] {
  const choirsById = new Map(data.choirs.map((choir) => [choir.choirId, choir]));

  return data.memberships
    .filter((membership) => membership.userId === userId)
    .map((membership) => {
      const choir = choirsById.get(membership.choirId);

      if (!choir) {
        throw new Error('Choir not found');
      }

      return choir;
    });
}

function isInvitationCodeCollision(error: unknown): boolean {
  return error instanceof Error && error.message === 'Invitation code already exists';
}

function findMembership(data: MemberManagementData, userId: string, choirId: string): Membership | null {
  return data.memberships.find((membership) => membership.userId === userId && membership.choirId === choirId) ?? null;
}

function getCurrentSeasonId(data: MemberManagementData, choirId: string): string {
  const currentSeasons = data.seasons.filter((season) => season.choirId === choirId && season.isCurrent);

  if (currentSeasons.length !== 1) {
    throw new Error('Choir must have exactly one current season');
  }

  return currentSeasons[0].seasonId;
}

function getCurrentSeasonMembers(
  data: MemberManagementData,
  choirId: string,
  seasonId: string
): readonly MemberListItem[] {
  return data.seasonMemberships
    .filter((seasonMembership) => seasonMembership.choirId === choirId && seasonMembership.seasonId === seasonId)
    .map((seasonMembership) => toMemberListItem(data, seasonMembership));
}

function toMemberListItem(data: MemberManagementData, seasonMembership: SeasonMembership): MemberListItem {
  const membership = data.memberships.find((candidate) => candidate.membershipId === seasonMembership.membershipId);
  const memberGroupIds = data.seasonMembershipMemberGroups
    .filter((link) => link.seasonMembershipId === seasonMembership.seasonMembershipId)
    .map((link) => link.memberGroupId);
  const memberGroupNames = data.memberGroups
    .filter((memberGroup) => memberGroupIds.includes(memberGroup.memberGroupId))
    .sort((left, right) => left.displayOrder - right.displayOrder)
    .map((memberGroup) => memberGroup.name);

  if (!membership) {
    throw new Error('Membership not found');
  }

  return {
    seasonMembershipId: seasonMembership.seasonMembershipId,
    nickname: seasonMembership.nickname,
    role: membership.role,
    memberGroupNames
  };
}

function toMemberDisplayListItem(member: MemberListItem): MemberDisplayListItem {
  return {
    displayMemberId: deriveMemberDisplayId(member.seasonMembershipId),
    nickname: member.nickname,
    memberGroupNames: member.memberGroupNames
  };
}

function toMemberSelectorListItem(member: MemberListItem): MemberSelectorListItem {
  return {
    seasonMembershipId: member.seasonMembershipId,
    nickname: member.nickname,
    memberGroupNames: member.memberGroupNames
  };
}

function assertSupportedMemberSelectorPurpose(purpose: MemberSelectorPurpose): void {
  if (
    purpose !== 'member_group_initial_member' &&
    purpose !== 'member_group_add_member' &&
    purpose !== 'member_selection'
  ) {
    throw new Error('Unsupported member selector purpose');
  }
}

function toMemberGroupSummary(
  data: MemberManagementData,
  memberGroup: MemberGroup,
  currentMembership: Membership
): MemberGroupSummary {
  return {
    memberGroupId: memberGroup.memberGroupId,
    name: memberGroup.name,
    isDefault: memberGroup.isDefault,
    memberCount: countMemberGroupMembers(data, memberGroup.memberGroupId),
    canManage: canManageMemberGroup(data, memberGroup, currentMembership)
  };
}

function toMemberGroupDetail(
  data: MemberManagementData,
  memberGroup: MemberGroup,
  currentMembership: Membership
): MemberGroupDetail {
  const memberGroupMemberIds = new Set(
    data.seasonMembershipMemberGroups
      .filter((link) => link.memberGroupId === memberGroup.memberGroupId)
      .map((link) => link.seasonMembershipId)
  );
  const memberGroupTags = data.memberGroupTags
    .filter((tag) => tag.memberGroupId === memberGroup.memberGroupId)
    .sort((left, right) => left.displayOrder - right.displayOrder);
  const memberCount = memberGroupMemberIds.size;
  const canManage = canManageMemberGroup(data, memberGroup, currentMembership);
  const members = data.seasonMemberships
    .filter((seasonMembership) => memberGroupMemberIds.has(seasonMembership.seasonMembershipId))
    .map((seasonMembership) => toMemberGroupMemberListItem(data, seasonMembership, memberGroup, memberGroupTags));
  const availableMembersToAdd = data.seasonMemberships
    .filter(
      (seasonMembership) =>
        seasonMembership.choirId === memberGroup.choirId &&
        seasonMembership.seasonId === memberGroup.seasonId &&
        seasonMembership.status === 'active' &&
        !memberGroupMemberIds.has(seasonMembership.seasonMembershipId)
    )
    .map((seasonMembership) => toMemberListItem(data, seasonMembership));
  const tags = memberGroupTags.map((tag) => toMemberGroupTagListItem(data, tag));

  return {
    memberGroupId: memberGroup.memberGroupId,
    name: memberGroup.name,
    isDefault: memberGroup.isDefault,
    memberCount,
    canManage,
    canAddMembers: canAddMembersToMemberGroup(data, memberGroup, currentMembership),
    members,
    availableMembersToAdd,
    tags
  };
}

function toMemberGroupMemberListItem(
  data: MemberManagementData,
  seasonMembership: SeasonMembership,
  memberGroup: MemberGroup,
  memberGroupTags: readonly MemberGroupTag[]
): MemberGroupMemberListItem {
  const membership = data.memberships.find((candidate) => candidate.membershipId === seasonMembership.membershipId);

  if (!membership) {
    throw new Error('Membership not found');
  }

  const assignedTagIds = new Set(
    data.seasonMembershipMemberGroupTags
      .filter((link) => link.seasonMembershipId === seasonMembership.seasonMembershipId)
      .map((link) => link.memberGroupTagId)
  );
  const assignedTags = memberGroupTags.filter((tag) => assignedTagIds.has(tag.memberGroupTagId));
  const isImplicitAdminManager = membership.role === 'admin';

  return {
    seasonMembershipId: seasonMembership.seasonMembershipId,
    nickname: seasonMembership.nickname,
    role: membership.role,
    tagIds: assignedTags.map((tag) => tag.memberGroupTagId),
    tagNames: assignedTags.map((tag) => tag.name),
    isGroupManager: canManageMemberGroupByRoleOrGrant({
      memberRole: membership.role,
      currentSeasonMembershipId: seasonMembership.seasonMembershipId,
      memberGroupId: memberGroup.memberGroupId,
      managerGrants: data.memberGroupManagerGrants
    }),
    isImplicitAdminManager
  };
}

function toMemberGroupTagListItem(data: MemberManagementData, memberGroupTag: MemberGroupTag): MemberGroupTagListItem {
  return {
    memberGroupTagId: memberGroupTag.memberGroupTagId,
    name: memberGroupTag.name,
    memberCount: data.seasonMembershipMemberGroupTags.filter(
      (link) => link.memberGroupTagId === memberGroupTag.memberGroupTagId
    ).length
  };
}

function toPermissionGroupSummary(
  data: MemberManagementData,
  permissionGroup: PermissionGroup
): PermissionGroupSummary {
  return {
    permissionGroupId: permissionGroup.permissionGroupId,
    name: permissionGroup.name,
    permissionKeys: getPermissionGroupPermissionKeys(data, permissionGroup.permissionGroupId),
    memberCount: countPermissionGroupMembers(data, permissionGroup.permissionGroupId),
    createdAt: permissionGroup.createdAt,
    updatedAt: permissionGroup.updatedAt
  };
}

function toPermissionGroupDetail(data: MemberManagementData, permissionGroup: PermissionGroup): PermissionGroupDetail {
  const seasonMembershipsById = new Map(
    data.seasonMemberships.map((seasonMembership) => [seasonMembership.seasonMembershipId, seasonMembership])
  );
  const members = data.permissionGroupMembers
    .filter((member) => member.permissionGroupId === permissionGroup.permissionGroupId)
    .map((member) => {
      const seasonMembership = seasonMembershipsById.get(member.seasonMembershipId);

      if (!seasonMembership) {
        throw new Error('Permission group member season membership not found');
      }

      return toPermissionGroupMemberListItem(data, seasonMembership);
    });

  return {
    permissionGroupId: permissionGroup.permissionGroupId,
    name: permissionGroup.name,
    permissionKeys: getPermissionGroupPermissionKeys(data, permissionGroup.permissionGroupId),
    members,
    createdAt: permissionGroup.createdAt,
    updatedAt: permissionGroup.updatedAt
  };
}

function toPermissionGroupMemberListItem(
  data: MemberManagementData,
  seasonMembership: SeasonMembership
): PermissionGroupMemberListItem {
  const member = toMemberListItem(data, seasonMembership);

  return {
    seasonMembershipId: member.seasonMembershipId,
    nickname: member.nickname,
    memberGroups: member.memberGroupNames
  };
}

function getPermissionGroupPermissionKeys(
  data: MemberManagementData,
  permissionGroupId: string
): readonly PermissionKey[] {
  return data.permissionGroupPermissions
    .filter((permission) => permission.permissionGroupId === permissionGroupId)
    .map((permission) => permission.permissionKey);
}

function countPermissionGroupMembers(data: MemberManagementData, permissionGroupId: string): number {
  return data.permissionGroupMembers.filter((member) => member.permissionGroupId === permissionGroupId).length;
}

function comparePermissionGroupsByCreatedAt(left: PermissionGroup, right: PermissionGroup): number {
  return left.createdAt.localeCompare(right.createdAt) || left.name.localeCompare(right.name);
}

function findPermissionGroupForService(
  data: MemberManagementData,
  permissionGroupId: string,
  choirId: string,
  seasonId: string
): PermissionGroup {
  const permissionGroup = data.permissionGroups.find((candidate) => candidate.permissionGroupId === permissionGroupId);

  if (!permissionGroup) {
    throw new Error('Permission group not found');
  }

  if (permissionGroup.choirId !== choirId || permissionGroup.seasonId !== seasonId) {
    throw new Error('Permission group belongs to a different choir or season');
  }

  return permissionGroup;
}

function canManageMemberGroup(
  data: MemberManagementData,
  memberGroup: MemberGroup,
  currentMembership: Membership
): boolean {
  assertMembershipBelongsToMemberGroupChoir(currentMembership, memberGroup);

  const currentSeasonMembership =
    currentMembership.role === 'admin'
      ? null
      : findActiveSeasonMembershipForMemberGroup(data, currentMembership, memberGroup);

  return canManageMemberGroupByRoleOrGrant({
    memberRole: currentMembership.role,
    currentSeasonMembershipId: currentSeasonMembership?.seasonMembershipId,
    memberGroupId: memberGroup.memberGroupId,
    managerGrants: data.memberGroupManagerGrants
  });
}

function canAddMembersToMemberGroup(
  data: MemberManagementData,
  memberGroup: MemberGroup,
  currentMembership: Membership
): boolean {
  if (canManageMemberGroup(data, memberGroup, currentMembership)) {
    return true;
  }

  const currentSeasonMembership = findActiveSeasonMembershipForMemberGroup(data, currentMembership, memberGroup);

  if (!currentSeasonMembership) {
    return false;
  }

  return canCreatorAddFirstMemberToGroup({
    memberCount: countMemberGroupMembers(data, memberGroup.memberGroupId),
    createdBySeasonMembershipId: memberGroup.createdBySeasonMembershipId,
    currentSeasonMembershipId: currentSeasonMembership.seasonMembershipId
  });
}

function findMemberGroupForService(
  data: MemberManagementData,
  memberGroupId: string,
  currentMembership: Membership
): MemberGroup {
  const memberGroup = data.memberGroups.find((candidate) => candidate.memberGroupId === memberGroupId);

  if (!memberGroup) {
    throw new Error('Member group not found');
  }

  if (memberGroup.choirId !== currentMembership.choirId) {
    throw new Error('Member group belongs to a different choir');
  }

  if (memberGroup.seasonId !== getCurrentSeasonId(data, currentMembership.choirId)) {
    throw new Error('Member group is not in the current season');
  }

  return memberGroup;
}

function findActiveSeasonMembershipForMemberGroup(
  data: MemberManagementData,
  membership: Membership,
  memberGroup: MemberGroup
): SeasonMembership | null {
  assertMembershipBelongsToMemberGroupChoir(membership, memberGroup);

  return (
    data.seasonMemberships.find(
      (candidate) =>
        candidate.membershipId === membership.membershipId &&
        candidate.choirId === memberGroup.choirId &&
        candidate.seasonId === memberGroup.seasonId &&
        candidate.status === 'active'
    ) ?? null
  );
}

function assertMembershipBelongsToMemberGroupChoir(membership: Membership, memberGroup: MemberGroup): void {
  if (membership.choirId !== memberGroup.choirId) {
    throw new Error('Member group belongs to a different choir');
  }
}

function countMemberGroupMembers(data: MemberManagementData, memberGroupId: string): number {
  return data.seasonMembershipMemberGroups.filter((link) => link.memberGroupId === memberGroupId).length;
}

function toPendingJoinRequestListItem(
  data: MemberManagementData,
  joinRequest: JoinRequest
): PendingJoinRequestListItem {
  const applicant = data.users.find((user) => user.userId === joinRequest.userId);

  if (!applicant) {
    throw new Error('Join request applicant not found');
  }

  return {
    joinRequestId: joinRequest.joinRequestId,
    userId: joinRequest.userId,
    applicantEmail: applicant.email,
    choirId: joinRequest.choirId,
    seasonId: joinRequest.seasonId,
    invitationCodeId: joinRequest.invitationCodeId,
    nickname: joinRequest.nickname,
    status: 'pending',
    submittedAt: joinRequest.submittedAt
  };
}

function toApprovalListItem(data: MemberManagementData, joinRequest: JoinRequest): ApprovalListItem {
  const applicant = data.users.find((user) => user.userId === joinRequest.userId);

  if (!applicant) {
    throw new Error('Join request applicant not found');
  }

  return {
    joinRequestId: joinRequest.joinRequestId,
    nickname: joinRequest.nickname,
    applicantEmail: applicant.email,
    submittedAt: joinRequest.submittedAt
  };
}

function toApprovalDetail(data: MemberManagementData, joinRequest: JoinRequest): ApprovalDetail {
  return {
    ...toApprovalListItem(data, joinRequest),
    memberGroupChoices: data.memberGroups
      .filter((group) => group.choirId === joinRequest.choirId && group.seasonId === joinRequest.seasonId)
      .sort((left, right) => left.displayOrder - right.displayOrder)
      .map((group) => ({
        memberGroupId: group.memberGroupId,
        name: group.name,
        isDefault: group.isDefault,
        displayOrder: group.displayOrder
      }))
  };
}

function requirePendingApprovalJoinRequestForWorkspace(
  data: MemberManagementData,
  input: {
    readonly joinRequestId: string;
    readonly choirId: string;
    readonly seasonId: string;
  }
): JoinRequest {
  const joinRequest = data.joinRequests.find((candidate) => candidate.joinRequestId === input.joinRequestId);

  if (
    !joinRequest ||
    joinRequest.choirId !== input.choirId ||
    joinRequest.seasonId !== input.seasonId ||
    joinRequest.status !== 'pending'
  ) {
    throw new Error('Join request not found');
  }

  return joinRequest;
}

function toInvitationListItem(invitationCode: InvitationCode): InvitationListItem {
  return {
    invitationCodeId: invitationCode.invitationCodeId,
    code: invitationCode.code,
    status: invitationCode.status,
    createdAt: invitationCode.createdAt,
    expiresAt: invitationCode.expiresAt
  };
}
